import { LitElement, html, type PropertyValues } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TUTORIAL_EVENT_TYPES, flowyWelcomeShown, tutorialEmitter } from '../tutorials/tutorialEvents';
import { marked } from 'marked';
import * as generateUi from '../ui/generateUi';
import * as searchUi from '../ui/searchUi';
import * as sparkUi from '../ui/sparkUi';
import * as empathiseUi from '../ui/empathiseUi';
import { FLOWY_LONG_MESSAGE_CHARS, SYNTHESISE_AI_TASKS } from '../../../../src/commonConstants';
import { getUserLikes, shareLikeToTeam } from '../apiRequests';
import { findMentionedBoardNodeIds } from '../../../../src/ai/agent/nodeMentions';
import { hostParticipantJoinedSummary } from '../../../../src/ai/agent/boardContentSummary';
import { truncateFlowyText } from '../../../../src/ai/agent/textTruncation';
import {
	formatNodeAddedStatus,
	formatPdfSeedAddedStatus,
	getEventStatusSummary,
	hasExpandableEventDetails,
} from './flowyStatusMessages';
import {
	shouldAllowFlowyAutoSelectNodes,
	findActiveQuickReplyMessageIndex,
	findOpeningQuickReplyMessageIndex,
	shouldShowDirectionPrompt,
} from './flowyQuickReplies';
import {
	isMediaAddToBoardQuickReply,
	isMediaLikeQuickReply,
} from '../ui/flowyGenerateActions';
import { normalizeFlowyMarkdown } from './flowyMarkdown';
import {
	FLOWY_CONTEXT_MODE_CHANGING_EVENT,
	getFlowyContextModeStatus,
	type FlowyContextModeChangingDetail,
	type FlowyContextModeStatus,
} from './flowyContextMode';

type AgentNodeReference = {
	uid: string;
	label: string;
	colour?: string;
	nodeColour?: string;
};

type FlowyPlanBoard = 'inspire' | 'ideate' | 'moodboard';

type AgentPlanStep = {
	id: string;
	label: string;
	status: string;
	toolHint?: string;
	buildingBlockId?: string;
	board?: FlowyPlanBoard;
	/** CSS variable e.g. --orange */
	colour?: string;
};

type AgentPlan = {
	steps: AgentPlanStep[];
} | null;

type FlowyBuildingBlockOption = {
	id: string;
	label: string;
	board: FlowyPlanBoard;
	colourVar: string;
};

type SharedFlowySession = {
	sharedSessionId: string;
	_hostUserId?: string | { toString?: () => string };
	goal?: string | null;
	plan?: AgentPlan;
	planAccepted?: boolean;
	activeStepId?: string | null;
	activeBuildingBlockId?: string | null;
	activeBoard?: FlowyPlanBoard | null;
	completedStepIds?: string[];
	lastAdvancedAt?: string | null;
};

type FlowyParticipantState = {
	role?: 'host' | 'participant';
	presenceStatus?: 'online' | 'left';
	joinedStepId?: string | null;
	personalSessionId?: string;
	lastSeenActivityId?: string | null;
};

type SharedPlanState = {
	sharedSession: SharedFlowySession | null;
	participantState: FlowyParticipantState | null;
};

type SharedFlowyActivity = {
	_id: string;
	actorUserId?: string | { toString?: () => string } | null;
	actorName?: string | null;
	type: string;
	summaryText?: string | null;
	entityRefs?: Array<{ type: string; id?: string | null; label?: string | null }>;
	metadata?: Record<string, unknown> | null;
	createdAt?: string;
};

type NextStepHint = {
	buildingBlockId: string;
	board: FlowyPlanBoard;
	label: string;
	sequenceId?: string | null;
};

type AgentQuickReply = {
	id: string;
	label: string;
	value: string;
	target?: {
		type: 'positioning-output' | 'board-node';
		id?: string;
		uid?: string;
		title?: string;
		label?: string;
	};
};

const FLOWY_RETRY_UNAVAILABLE_MESSAGE = 'Flowy could not respond just now.';
const FLOWY_RETRY_QUICK_REPLY: AgentQuickReply = {
	id: 'flowy_retry',
	label: 'Retry',
	value: '__flowy_retry__',
};
const FLOWY_CONTINUE_TO_BLOCK_PREFIX = '__flowy_continue_to_block__:';
const FLOWY_STAY_ON_BLOCK_PREFIX = '__flowy_stay_on_block__:';
const FLOWY_SHARE_TO_TEAM_VALUE = '__flowy_share_to_team__';
const FLOWY_VIEW_POSITIONING_FOUNDATIONS_VALUE = '__flowy_view_positioning_foundations__';
const FLOWY_CONFIGURE_POSITIONING_FOUNDATIONS_VALUE =
	'__flowy_configure_positioning_foundations__';
const parseFlowyMarkdown = (content: string): string =>
	marked.parse(normalizeFlowyMarkdown(content), { async: false }) as string;
const FLOWY_JOIN_SHARED_STEP_PREFIX = '__flowy_join_shared_step__:';
const SHARED_ACTIVITY_ASSISTANT_TYPES = new Set(['shared_plan_advanced', 'board_switched']);
const SHARED_ACTIVITY_IGNORED_TYPES = new Set([
	'participant_joined_session',
	'participant_left_session',
	'participant_joined_step',
	'node_created',
	'node_updated',
	'node_deleted',
	'node_liked',
	'cluster_created',
	'cluster_updated',
	'cluster_deleted',
	'tool_results_materialized',
	'shared_plan_accepted',
]);

type AgentPanelMessage = {
	role: 'user' | 'assistant' | 'event' | 'tool';
	content: string;
	nextStepHint?: NextStepHint | null;
	quickReplies?: AgentQuickReply[];
};

type PendingNodeAddedStatus = {
	label: string;
	pdfAnalysisId?: string;
};

const NODE_ADDED_STATUS_DEBOUNCE_MS = 700;

type FlowyEffect =
	| {
			type: 'assistant_message';
			message: string;
			nextStepHint?: NextStepHint | null;
			quickReplies?: AgentQuickReply[];
	  }
	| {
			type: 'open_tool_panel';
			panel: string;
			task?: string | null;
			subtask?: string | null;
			selectedNodeIds?: string[];
			selectedNodeLabels?: string[];
	  }
	| {
			type: 'show_tool_results';
			panel: string;
			tool: string;
			task?: string | null;
			subtask?: string | null;
			results: Array<Record<string, unknown>>;
			selectedNodeIds?: string[];
			selectedNodeLabels?: string[];
			recommendedResultId?: string | null;
	  }
	| {
			type: string;
			[key: string]: unknown;
	  };

type FlowySessionLogEntry = {
	index: number;
	ts: string;
	elapsedMs: number;
	kind: string;
	boardId: string;
	sessionId: string;
	payload: unknown;
};

type AgentSessionStateLite = {
	activeBoard?: FlowyPlanBoard;
	activeBuildingBlockId?: string | null;
	activeSequenceId?: string | null;
	completedBuildingBlockIds?: string[] | null;
	ideateContextMode?: 'personal' | 'team' | null;
	synthesiseContextMode?: 'personal' | 'team' | null;
	modeContextVersion?: number | null;
};

type ResearchLensSummary = {
	id: string;
	label: string;
	purpose: string;
	preferredSources: string[];
};

type OnboardingWelcomeContext = {
	onboardingStatus: 'completed' | 'skipped';
	input?: string | null;
	vision?: string | null;
	summary?: string | null;
	selectedText?: string | null;
	centralTitle?: string | null;
	useCase?: string | null;
	templateId?: string | null;
	seeds?: Array<{ label: string; lens: string }> | null;
};

type ToolStep = {
	callId: string;
	name: string;
	label: string;
	rationale?: string;
	arguments?: string;
	status: 'running' | 'completed' | 'failed';
};

type AgentHttpReply = {
	message: string;
	goal: string | null;
	plan: AgentPlan;
	planAccepted?: boolean;
	toolMessages?: string[];
	nodeRefs?: AgentNodeReference[];
	quickReplies?: AgentQuickReply[];
	effects?: FlowyEffect[];
};

type AgentEvent =
	| {
			type: 'start';
			sessionId: string;
			boardId: string;
			message: string;
	  }
	| {
			type: 'plan';
			sessionId: string;
			boardId: string;
			plan: AgentPlan;
	  }
	| {
			type: 'tool_call';
			sessionId: string;
			boardId: string;
			callId: string;
			name: string;
			label: string;
			rationale?: string;
			arguments: string;
			effects?: FlowyEffect[];
	  }
	| {
			type: 'tool_result';
			sessionId: string;
			boardId: string;
			callId: string;
			nodeRefs?: AgentNodeReference[];
			output: string;
			effects?: FlowyEffect[];
	  }
	| {
			type: 'assistant_delta';
			sessionId: string;
			boardId: string;
			delta: string;
	  }
	| {
			type: 'final_message';
			sessionId: string;
			boardId: string;
			message: string;
			goal: string | null;
			plan: AgentPlan;
			planAccepted?: boolean;
			quickReplies?: AgentQuickReply[];
			effects?: FlowyEffect[];
			nodeRefs?: AgentNodeReference[];
			materializedNodeIds?: string[];
	  }
	| {
			type: 'plan_accepted';
			sessionId: string;
			boardId: string;
	  }
	| {
			type: 'done';
			sessionId: string;
			boardId: string;
	  }
	| {
			type: 'error';
			sessionId: string;
			boardId: string;
			message: string;
	  }
	| {
			type: 'tool_timeline';
			sessionId: string;
			boardId: string;
			message: string;
	  };

declare global {
	interface Window {
		subscribeAgentSession?: (
			sessionId: string,
			onEvent: (event: AgentEvent) => void
		) => () => void;
		__flowySessionLogs?: Record<string, FlowySessionLogEntry[]>;
		exportFlowySessionLog?: (sessionId?: string) => string;
	}
}

const createSessionId = () =>
	`flowy-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getSessionStorageKey = (boardId: string) => `flowy-session-id:${boardId}`;
const FLOWY_LOG_STORAGE_KEY = 'flowy-session-logs';
const MAX_FLOWY_SESSION_LOG_ENTRIES = 2500;
const IDLE_ASSIST_CHECK_SCHEDULE_MS = [180_000, 300_000, 600_000] as const;
const IDLE_ASSIST_MESSAGE_COOLDOWN_MS = 60_000;
const IDLE_ASSIST_RECENT_EVENT_GUARD_MS = 10_000;

const GENERATED_RESULT_EVENT_TYPES = new Set<string>([
	TUTORIAL_EVENT_TYPES.sparkResultsGenerated,
	TUTORIAL_EVENT_TYPES.empathiseResultsGenerated,
	TUTORIAL_EVENT_TYPES.brainstormResultsGenerated,
	TUTORIAL_EVENT_TYPES.commonIdeasResultsGenerated,
	TUTORIAL_EVENT_TYPES.haikuResultsGenerated,
	TUTORIAL_EVENT_TYPES.aiHaikuResultsGenerated,
	TUTORIAL_EVENT_TYPES.taglineResultsGenerated,
	TUTORIAL_EVENT_TYPES.storyResultsGenerated,
	TUTORIAL_EVENT_TYPES.characterResultsGenerated,
	TUTORIAL_EVENT_TYPES.brandColoursResultsGenerated,
	TUTORIAL_EVENT_TYPES.searchResultsGenerated,
	TUTORIAL_EVENT_TYPES.personaResultsGenerated,
	TUTORIAL_EVENT_TYPES.userPersonasGenerated,
	TUTORIAL_EVENT_TYPES.stakeholderMotivationsGenerated,
	TUTORIAL_EVENT_TYPES.audiencesGenerated,
	TUTORIAL_EVENT_TYPES.imageInspireGenerated,
	TUTORIAL_EVENT_TYPES.videoInspireGenerated,
	TUTORIAL_EVENT_TYPES.imageSynthesiseGenerated,
]);

const CONTEXT_CHANGING_TUTORIAL_EVENT_TYPES = new Set<string>([
	TUTORIAL_EVENT_TYPES.clickedSpark,
	TUTORIAL_EVENT_TYPES.clickedEmpathise,
	TUTORIAL_EVENT_TYPES.clickedBrainstorm,
	TUTORIAL_EVENT_TYPES.clickedCommonIdeas,
	TUTORIAL_EVENT_TYPES.clickedSearch,
	TUTORIAL_EVENT_TYPES.clickedHaikus,
	TUTORIAL_EVENT_TYPES.clickedAIHaikus,
	TUTORIAL_EVENT_TYPES.clickedTaglines,
	TUTORIAL_EVENT_TYPES.clickedGenerateImage,
	TUTORIAL_EVENT_TYPES.clickedGenerateUserPersona,
	TUTORIAL_EVENT_TYPES.clickedGenerateMotivations,
	TUTORIAL_EVENT_TYPES.clickedPersonalityAnalysis,
	TUTORIAL_EVENT_TYPES.clickedThemesAnalysis,
	TUTORIAL_EVENT_TYPES.clickedPlotsAnalysis,
	TUTORIAL_EVENT_TYPES.clickedVisualAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationsAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationChallengesAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationHarmonyAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationsStimulationAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationsNoveltyAnalysis,
	TUTORIAL_EVENT_TYPES.clickedMotivationsThreatAnalysis,
	TUTORIAL_EVENT_TYPES.switchBoard,
]);

const STARTUP_SUPPRESSED_EVENT_TYPES = new Set<string>([
	TUTORIAL_EVENT_TYPES.createdNode,
	TUTORIAL_EVENT_TYPES.addedSingleNode,
	TUTORIAL_EVENT_TYPES.addedTwoNodes,
	TUTORIAL_EVENT_TYPES.addedWordsFromPanel,
	TUTORIAL_EVENT_TYPES.updatedLabel,
	TUTORIAL_EVENT_TYPES.switchBoard,
]);

const AUTO_SPARK_SUPPRESSED_EVENT_TYPES = new Set<string>([
	TUTORIAL_EVENT_TYPES.clickedSpark,
	TUTORIAL_EVENT_TYPES.sparkResultsGenerated,
	TUTORIAL_EVENT_TYPES.clickedSparkResults,
	TUTORIAL_EVENT_TYPES.addedTwoNodes,
	TUTORIAL_EVENT_TYPES.addedSingleNode,
	TUTORIAL_EVENT_TYPES.afterAutoSpark,
	TUTORIAL_EVENT_TYPES.updatedLabel,
	TUTORIAL_EVENT_TYPES.autoSparkStarted,
	TUTORIAL_EVENT_TYPES.autoSparkFinished,
	TUTORIAL_EVENT_TYPES.bootstrapAutoSparkFinished,
]);

type FlowySlashCommand = {
	label: string;
	value: string;
	hint: string;
};

const INSPIRE_SLASH_COMMANDS: FlowySlashCommand[] = [
	{
		label: 'Spark',
		value: 'Spark around this node',
		hint: 'Open adjacent phrases for the selected node',
	},
	{
		label: 'Spark + add',
		value: 'Spark around this node and add 1-2 best results to the board',
		hint: 'Open adjacent phrases and place a small curated set',
	},
	{
		label: 'Empathise',
		value: 'Empathise around this node',
		hint: 'Surface needs, tensions, and perspectives',
	},
	{
		label: 'Empathise + add',
		value: 'Empathise around this node and add 1-2 best results to the board',
		hint: 'Surface perspectives and place a small curated set',
	},
	{
		label: 'Brainstorm',
		value: 'Brainstorm around this node',
		hint: 'Generate grounded idea routes',
	},
	{
		label: 'Brainstorm + add',
		value: 'Brainstorm around this node and add the best results to the board',
		hint: 'Generate routes and place selected ideas',
	},
	{
		label: 'Search',
		value: 'Search around this node',
		hint: 'Find external references and highlight results',
	},
	{
		label: 'Search + add',
		value: 'Search around this node and add nodes to the board',
		hint: 'Find references and place selected nodes',
	},
	{
		label: 'Common ideas',
		value: 'Find common ideas around this node',
		hint: 'Connect selected concepts',
	},
	{
		label: 'Common ideas + add',
		value: 'Find common ideas around this node and add the best results to the board',
		hint: 'Connect concepts and place selected bridges',
	},
	{
		label: 'Taglines',
		value: 'Generate taglines around this node',
		hint: 'Create short naming and messaging options',
	},
	{
		label: 'Haikus',
		value: 'Generate haikus around this node',
		hint: 'Create poetic inspiration lines',
	},
	{
		label: 'Image',
		value: 'Generate an image around this node',
		hint: 'Create a visual direction from the selected node',
	},
	{
		label: 'Video',
		value: 'Generate a video around this node',
		hint: 'Create a short motion reference from the selected node',
	},
	{
		label: 'AI Haikus',
		value: 'Generate AI haikus around this node',
		hint: 'Create AI-written poetic inspiration lines',
	},
];

const IDEATE_SLASH_COMMANDS: FlowySlashCommand[] = [
	{
		label: 'Persona',
		value: 'Generate personas around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create persona cards to heart into the Idea Box',
	},
	{
		label: 'Motivations',
		value: 'Generate stakeholder motivations around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create motivation cards to heart into the Idea Box',
	},
	{
		label: 'Taglines',
		value: 'Generate taglines around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create messaging options to heart',
	},
	{
		label: 'Haiku',
		value: 'Generate haikus around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create poetic options to heart',
	},
	{
		label: 'Search',
		value: 'Search around this insight; show results to heart, do not add nodes',
		hint: 'Find references without placing board nodes',
	},
	{
		label: 'Video',
		value: 'Generate a video around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create motion options to heart',
	},
	{
		label: 'AI Haiku',
		value: 'Generate AI haikus around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Create AI-written poetic options to heart',
	},
	{
		label: 'Brainstorm',
		value: 'Brainstorm around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Generate idea routes to heart',
	},
	{
		label: 'Common ideas',
		value: 'Find common ideas around this insight for the Idea Box; show results to heart, do not add nodes',
		hint: 'Connect insights and heart the strongest bridges',
	},
];

const humanizeSynthesiseTaskId = (taskId: string): string =>
	taskId.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const SYNTHESISE_SLASH_COMMANDS: FlowySlashCommand[] = Object.entries(SYNTHESISE_AI_TASKS).flatMap(
	([taskId, task]) => {
		const label = task.label || humanizeSynthesiseTaskId(taskId);
		const command: FlowySlashCommand = {
			label,
			value: `Synthesise ${label} around this node`,
			hint: task.name,
		};
		const optionCommands = Object.entries(task.options || {}).map(([optionId, option]) => ({
			label: `${label}: ${option.optionName}`,
			value: `Synthesise ${label}: ${option.name} around this node`,
			hint: humanizeSynthesiseTaskId(optionId),
		}));
		return [command, ...optionCommands];
	}
);

export class AgentChatPanel extends LitElement {
	static properties = {
		boardId: { type: String, attribute: 'board-id' },
		sessionId: { type: String, attribute: 'session-id' },
		isBusy: { state: true },
		messages: { state: true },
		workingLabel: { state: true },
		plan: { state: true },
		planAccepted: { state: true },
		sharedPlanState: { state: true },
		sessionGoal: { state: true },
		streamingAssistantContent: { state: true },
		toolSteps: { state: true },
		activePlanStepId: { state: true },
		isMinimized: { state: true },
		isPlanCollapsed: { state: true },
		isSlashCommandMenuOpen: { state: true },
		expandedMessageIndexes: { state: true },
		directionPromptDismissed: { state: true },
		directionPromptExpanded: { state: true },
		isPlanAddMenuOpen: { state: true },
		isPlanSaving: { state: true },
		contextModeStatus: { state: true },
	};

	private tutorialEventListeners: Array<{ eventType: string; listener: (data: unknown) => void }> = [];
	private attachedTutorialEventTypes: Set<string> = new Set();
	private pendingPlanStepSwitch:
		| {
				board: FlowyPlanBoard;
				stepId: string;
				buildingBlockId: string | null;
				source:
					| 'plan_step_select'
					| 'shared_step_join'
					| 'host_step_advance'
					| 'plan_acceptance';
				emitGuidance?: boolean;
		  }
		| null = null;
	private lastSessionState: AgentSessionStateLite | null = null;
	boardId = '';
	sessionId = '';
	isBusy = false;
	messages: AgentPanelMessage[] = [];
	workingLabel = '';
	plan: AgentPlan = null;
	planAccepted = false;
	sharedPlanState: SharedPlanState | null = null;
	sessionGoal: string | null = null;
	researchLenses: ResearchLensSummary[] = [];
	private planBuildingBlocks: FlowyBuildingBlockOption[] = [];
	lastUsedResearchLensLabels: string[] = [];
	boardEngine = '';
	streamingAssistantContent = '';
	toolSteps: ToolStep[] = [];
	activePlanStepId: string | null = null;
	isMinimized = true;
	isPlanCollapsed = false;
	isSlashCommandMenuOpen = false;
	expandedMessageIndexes: number[] = [];
	directionPromptDismissed = false;
	directionPromptExpanded = false;
	isPlanAddMenuOpen = false;
	isPlanSaving = false;
	contextModeStatus: FlowyContextModeStatus | null = null;
	private unsubscribe: (() => void) | null = null;
	private historyPollTimer: number | null = null;
	private historyPollStartTimer: number | null = null;
	private sharedActivityPollTimer: number | null = null;
	private lastSharedActivityId: string | null = null;
	private sharedLeaveReported = false;
	private autoCatchupRequestedForSharedSessionId: string | null = null;
	private autoCatchupRetryScheduledForSharedSessionId: string | null = null;
	/** Skip board-entry recap while history/catch-up runs — catch-up already covers session juice. */
	private omitBoardEntrySummary = false;
	private expectedAssistantMessages = 0;
	private pendingRetryMessage: { message: string; display: string } | null = null;
	private receivedLiveEvent = false;
	private pendingNodeFocusTimer: number | null = null;
	private pendingBoardCenterFrame: number | null = null;
	private isDragging = false;
	private dragPointerId: number | null = null;
	private dragOffsetX = 0;
	private dragOffsetY = 0;
	/** Prevents duplicate POST /agent/welcome for the same session. */
	private welcomeRequestedForSession: string | null = null;
	private emitFlowyWelcomeShownOnReply = false;
	private boardInitialized = false;
	private boardInitializationTimer: number | null = null;
	private expandedPanelRect: { left: number; top: number; width: number; height: number } | null = null;
	private readonly panelTransitionMs = 300;
	private panelAnchorObserver: ResizeObserver | null = null;
	private userHasDraggedPanel = false;
	private idleAssistTimer: number | null = null;
	private idleCheckInFlight = false;
	private idleTimerToken = 0;
	private idleAssistStage = 0;
	private lastNonIdleEventAt = 0;
	private lastIdleMessage = '';
	private lastIdleMessageAt = 0;
	private announcedLiveToolCallIds = new Set<string>();
	private draggedPlanStepId: string | null = null;
	private toolCallNamesById = new Map<string, string>();
	private boardInteractionVersion = 0;
	private modeContextVersion = 0;
	private contextModeSyncQueue: Promise<void> = Promise.resolve();
	private pendingSessionGoalLabel: string | null = null;
	private suppressStartupEventsUntilWelcome = true;
	private startupEventSuppressionUntil = 0;
	private autoSparkSuppressionActive = false;
	private autoSparkSuppressionTimer: number | null = null;
	/** Hold all system commentary until the owner has chosen the first board goal. */
	private onboardingGoalPending = false;
	private deferredBootstrapTutorialPending = false;
	private pendingNodeAddedStatuses: PendingNodeAddedStatus[] = [];
	private nodeAddedStatusTimer: number | null = null;
	private sessionLogStartedAt = Date.now();
	private sessionLogEntries: FlowySessionLogEntry[] = [];
	private sessionLogCounter = 0;

	private isSessionLoggingEnabled(): boolean {
		const setting = window.localStorage.getItem(FLOWY_LOG_STORAGE_KEY);
		if (setting === '0' || setting === 'false') return false;
		if (setting === '1' || setting === 'true') return true;
		const params = new URLSearchParams(window.location.search);
		if (params.get('flowyLogs') === '1') return true;
		return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
	}

	private sanitizeForSessionLog(value: unknown): unknown {
		const seen = new WeakSet<object>();
		try {
			return JSON.parse(
				JSON.stringify(value, (_key, entry) => {
					if (entry instanceof Event) {
						return {
							type: entry.type,
							target: entry.target instanceof Element ? entry.target.tagName.toLowerCase() : null,
						};
					}
					if (entry && typeof entry === 'object') {
						if (seen.has(entry)) {
							return '[Circular]';
						}
						seen.add(entry);
					}
					if (typeof entry === 'function') {
						return `[Function ${entry.name || 'anonymous'}]`;
					}
					return entry;
				})
			);
		} catch {
			return String(value);
		}
	}

	private recordSessionLog(kind: string, payload: Record<string, unknown>): void {
		if (!this.isSessionLoggingEnabled()) return;
		const entry: FlowySessionLogEntry = {
			index: ++this.sessionLogCounter,
			ts: new Date().toISOString(),
			elapsedMs: Date.now() - this.sessionLogStartedAt,
			kind,
			boardId: this.boardId,
			sessionId: this.sessionId,
			payload: this.sanitizeForSessionLog(payload),
		};
		this.sessionLogEntries = [...this.sessionLogEntries, entry].slice(-MAX_FLOWY_SESSION_LOG_ENTRIES);
		window.__flowySessionLogs = window.__flowySessionLogs || {};
		window.__flowySessionLogs[this.sessionId] = this.sessionLogEntries;
		window.exportFlowySessionLog = (sessionId = this.sessionId) => {
			const entries = window.__flowySessionLogs?.[sessionId] || [];
			return JSON.stringify(
				{
					exportedAt: new Date().toISOString(),
					boardId: this.boardId,
					sessionId,
					entryCount: entries.length,
					entries,
				},
				null,
				2
			);
		};
	}

	private logDebug(
		type:
			| 'TUTORIAL_EVENT'
			| 'AGENT_EVENT'
			| 'USER_MESSAGE'
			| 'ASSISTANT_MESSAGE'
			| 'BT_MESSAGE'
			| 'SYSTEM_EVENT',
		payload: Record<string, unknown>
	) {
		const ts = new Date().toISOString();
		console.log(`[FlowyDebug][${ts}][${type}]`, payload);
		this.recordSessionLog(type, payload);
	}

	createRenderRoot() {
		return this;
	}

	connectedCallback(): void {
		super.connectedCallback();
		if (!this.sessionId) {
			const storedSessionId = this.boardId ? window.localStorage.getItem(getSessionStorageKey(this.boardId)) : null;
			this.sessionId = storedSessionId || createSessionId();
			this.setAttribute('session-id', this.sessionId);
			if (this.boardId) {
				window.localStorage.setItem(getSessionStorageKey(this.boardId), this.sessionId);
			}
		}
		this.restoreDirectionPromptDismissal();
		this.attachTutorialEventListeners();
		this.subscribe();
		void this.loadHistory();
		window.addEventListener('pagehide', this.reportSharedSessionLeave);
		window.addEventListener(
			FLOWY_CONTEXT_MODE_CHANGING_EVENT,
			this.handleContextModeChanging as EventListener
		);
		this.attachPanelAnchorObserver();
	}

	private attachPanelAnchorObserver() {
		window.addEventListener('resize', this.syncPanelAnchorPosition);
		this.panelAnchorObserver = new ResizeObserver(() => this.syncPanelAnchorPosition());
		const inspireProgress = document.getElementById('inspire-progress');
		if (inspireProgress) {
			this.panelAnchorObserver.observe(inspireProgress);
		}
		const switchWrap = document.querySelector('.board-switch-wrap');
		if (switchWrap) {
			this.panelAnchorObserver.observe(switchWrap);
		}
		requestAnimationFrame(() => this.syncPanelAnchorPosition());
	}

	private syncPanelAnchorPosition = () => {
		if (this.userHasDraggedPanel) {
			return;
		}
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}
		if (window.matchMedia('(max-width: 920px)').matches) {
			panel.style.removeProperty('--agent-chat-panel-top');
			return;
		}

		const gapPx = 12;
		const inspireProgress = document.getElementById('inspire-progress');
		const switchWrap = document.querySelector<HTMLElement>('.board-switch-wrap');
		const anchor =
			inspireProgress && !inspireProgress.classList.contains('hidden')
				? inspireProgress
				: switchWrap;

		if (!anchor) {
			panel.style.removeProperty('--agent-chat-panel-top');
			return;
		}

		const rect = anchor.getBoundingClientRect();
		if (rect.bottom > 0) {
			panel.style.setProperty('--agent-chat-panel-top', `${Math.round(rect.bottom + gapPx)}px`);
		}
	};

	private updateContextModeStatus(
		graphMode: FlowyPlanBoard,
		isCollaborative: boolean
	): void {
		this.contextModeStatus = getFlowyContextModeStatus(graphMode, isCollaborative);
		this.requestUpdate();
	}

	private invalidateIdleAssistanceForContextChange(): void {
		this.idleTimerToken += 1;
		if (this.idleAssistTimer !== null) {
			window.clearTimeout(this.idleAssistTimer);
			this.idleAssistTimer = null;
		}
		this.idleCheckInFlight = false;
		this.idleAssistStage = 0;
		this.lastIdleMessage = '';
		this.lastIdleMessageAt = 0;
		this.lastNonIdleEventAt = Date.now();
		if (this.workingLabel === 'Thinking...') {
			this.workingLabel = '';
		}
		this.streamingAssistantContent = '';
		this.resetIdleAssistTimer();
		this.requestUpdate();
	}

	private handleContextModeChanging = (
		event: CustomEvent<FlowyContextModeChangingDetail>
	): void => {
		const { graphMode, isCollaborative } = event.detail;
		if (graphMode !== 'ideate' && graphMode !== 'moodboard') return;

		this.modeContextVersion = Math.max(this.modeContextVersion + 1, Date.now());
		this.boardInteractionVersion += 1;
		const contextVersion = this.modeContextVersion;
		this.updateContextModeStatus(graphMode, isCollaborative);
		this.invalidateIdleAssistanceForContextChange();
		this.logDebug('SYSTEM_EVENT', {
			message: 'context_mode_changing',
			graphMode,
			isCollaborative,
			modeContextVersion: contextVersion,
		});

		// Keep rapid toggles ordered on the server while allowing the visual mode
		// status to update immediately. Responses for superseded versions are
		// discarded by postAgentEvent.
		this.contextModeSyncQueue = this.contextModeSyncQueue
			.catch(() => undefined)
			.then(() =>
				this.postAgentEvent(
					'context_mode_changed',
					{
						board: graphMode,
						isCollaborative,
						modeContextVersion: contextVersion,
						source: 'personal_team_toggle',
					},
					contextVersion
				)
			);
	};

	disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.unsubscribe) {
			this.unsubscribe();
		}
		this.stopHistoryPolling();
		this.stopSharedActivityPolling();
		this.reportSharedSessionLeave();
		if (this.historyPollStartTimer !== null) {
			window.clearTimeout(this.historyPollStartTimer);
		}
		if (this.pendingNodeFocusTimer !== null) {
			window.clearTimeout(this.pendingNodeFocusTimer);
		}
		if (this.pendingBoardCenterFrame !== null) {
			window.cancelAnimationFrame(this.pendingBoardCenterFrame);
		}
		if (this.boardInitializationTimer !== null) {
			window.clearTimeout(this.boardInitializationTimer);
		}
		if (this.idleAssistTimer !== null) {
			window.clearTimeout(this.idleAssistTimer);
		}
		if (this.autoSparkSuppressionTimer !== null) {
			window.clearTimeout(this.autoSparkSuppressionTimer);
		}
		if (this.nodeAddedStatusTimer !== null) {
			window.clearTimeout(this.nodeAddedStatusTimer);
		}
		window.removeEventListener('pointermove', this.onDragMove);
		window.removeEventListener('pointerup', this.onDragEnd);
		window.removeEventListener('pointercancel', this.onDragEnd);
		window.removeEventListener('resize', this.syncPanelAnchorPosition);
		window.removeEventListener('pagehide', this.reportSharedSessionLeave);
		window.removeEventListener(
			FLOWY_CONTEXT_MODE_CHANGING_EVENT,
			this.handleContextModeChanging as EventListener
		);
		this.panelAnchorObserver?.disconnect();
		this.panelAnchorObserver = null;
		this.detachTutorialEventListeners();
	}

	private attachTutorialEventListeners(): void {
		Object.values(TUTORIAL_EVENT_TYPES).forEach((eventType) => {
			if (this.attachedTutorialEventTypes.has(eventType)) {
				return;
			}
			const listener = (data: unknown) => {
				this.handleTutorialEvent(eventType, data);
			};
			tutorialEmitter.on(eventType, listener);
			this.tutorialEventListeners.push({ eventType, listener });
			this.attachedTutorialEventTypes.add(eventType);
		});
	}

	private detachTutorialEventListeners(): void {
		for (const entry of this.tutorialEventListeners) {
			tutorialEmitter.off(entry.eventType, entry.listener);
		}
		this.tutorialEventListeners = [];
		this.attachedTutorialEventTypes.clear();
	}

	private handleTutorialEvent(eventType: string, data: unknown): void {
		this.logDebug('TUTORIAL_EVENT', { eventType, data });
		this.resetIdleAssistTimer();
		if (eventType === TUTORIAL_EVENT_TYPES.flowyOnboardingCompleted) {
			const eventData = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
			this.boardInitialized = true;
			this.onboardingGoalPending = true;
			this.startupEventSuppressionUntil = Date.now() + 5000;
			void this.requestSessionWelcome({
				onboardingStatus: 'completed',
				input: typeof eventData.input === 'string' ? eventData.input : null,
				vision: typeof eventData.vision === 'string' ? eventData.vision : null,
				summary: typeof eventData.summary === 'string' ? eventData.summary : null,
				selectedText: typeof eventData.selectedText === 'string' ? eventData.selectedText : null,
				centralTitle: typeof eventData.centralTitle === 'string' ? eventData.centralTitle : null,
				useCase: typeof eventData.useCase === 'string' ? eventData.useCase : null,
				templateId: typeof eventData.templateId === 'string' ? eventData.templateId : null,
				seeds: Array.isArray(eventData.seeds)
					? eventData.seeds.filter(
							(seed): seed is { label: string; lens: string } =>
								Boolean(
									seed &&
										typeof seed === 'object' &&
										typeof (seed as { label?: unknown }).label === 'string' &&
										typeof (seed as { lens?: unknown }).lens === 'string'
								)
						)
					: null,
			});
			return;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.flowyOnboardingSkipped) {
			this.boardInitialized = true;
			void this.requestSessionWelcome({ onboardingStatus: 'skipped' });
			return;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.autoSparkStarted) {
			this.autoSparkSuppressionActive = true;
			if (this.autoSparkSuppressionTimer !== null) {
				window.clearTimeout(this.autoSparkSuppressionTimer);
			}
			this.autoSparkSuppressionTimer = window.setTimeout(() => {
				this.autoSparkSuppressionActive = false;
				this.autoSparkSuppressionTimer = null;
			}, 30_000);
			return;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.autoSparkFinished) {
			if (this.autoSparkSuppressionTimer !== null) {
				window.clearTimeout(this.autoSparkSuppressionTimer);
			}
			this.autoSparkSuppressionTimer = window.setTimeout(() => {
				this.autoSparkSuppressionActive = false;
				this.autoSparkSuppressionTimer = null;
			}, 500);
			return;
		}
		if (
			this.autoSparkSuppressionActive &&
			AUTO_SPARK_SUPPRESSED_EVENT_TYPES.has(eventType)
		) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'suppressed_auto_spark_event',
				eventType,
				data,
			});
			return;
		}
		if (CONTEXT_CHANGING_TUTORIAL_EVENT_TYPES.has(eventType)) {
			this.boardInteractionVersion += 1;
		}
		if (this.suppressStartupEventsUntilWelcome && this.messages.length === 0) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'suppressed_pre_welcome_event',
				eventType,
				data,
			});
			return;
		}
		if (
			Date.now() < this.startupEventSuppressionUntil &&
			STARTUP_SUPPRESSED_EVENT_TYPES.has(eventType)
		) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'suppressed_startup_event',
				eventType,
				data,
			});
			return;
		}
		if (!this.boardInitialized) {
			if (eventType === TUTORIAL_EVENT_TYPES.addedWordsFromPanel) {
				this.scheduleBoardInitialization();
			}
			if (eventType !== TUTORIAL_EVENT_TYPES.enableChatbot && eventType !== TUTORIAL_EVENT_TYPES.disableChatbot) {
				this.logDebug('SYSTEM_EVENT', {
					message: 'suppressed_before_board_initialized',
					eventType,
					data,
				});
				return;
			}
		}
		if (this.shouldSuppressIdeateAnalysisClusterEvent(eventType)) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'suppressed_ideate_analysis_cluster_event',
				eventType,
				data,
			});
			return;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.pdfSummaryInsightsAdded) {
			const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
			const analysisId = typeof payload.analysisId === 'string' ? payload.analysisId : '';
			const title = typeof payload.title === 'string' ? payload.title : 'PDF document';
			this.discardPendingPdfNodeStatuses(analysisId);
			if (payload.suppressStatus === true) return;
			this.addNeutralEventMessage(formatPdfSeedAddedStatus(title));
			return;
		}
		if (
			eventType === TUTORIAL_EVENT_TYPES.addedSingleNode ||
			eventType === TUTORIAL_EVENT_TYPES.addedTwoNodes
		) {
			this.queueNodeAddedStatus(data);
		} else {
			const neutralEvent = this.formatTutorialEventToast(eventType, data);
			if (neutralEvent) {
				this.addNeutralEventMessage(neutralEvent);
			}
		}
		if (eventType === TUTORIAL_EVENT_TYPES.enableChatbot || eventType === TUTORIAL_EVENT_TYPES.disableChatbot) {
			return;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.switchBoard) {
			const eventData = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
			const board = eventData.graphMode;
			if (board === 'inspire' || board === 'ideate' || board === 'moodboard') {
				const isCollaborative = eventData.isCollaborative === true;
				this.updateContextModeStatus(board, isCollaborative);
				const pending = this.pendingPlanStepSwitch;
				const planStepHint =
					pending && pending.board === board
						? {
								stepId: pending.stepId,
								buildingBlockId: pending.buildingBlockId,
								source: pending.source,
								emitGuidance: pending.emitGuidance !== false,
							}
						: undefined;
				const buildingBlockId = planStepHint?.buildingBlockId || null;
				this.pendingPlanStepSwitch = null;
				this.syncActivePlanStepForBoard(board, planStepHint);
				if (planStepHint) {
					this.centerBoardAfterPlanNavigation();
				}
				if (planStepHint && planStepHint.emitGuidance === false) {
					return;
				}
				const omitBoardSummary = this.shouldOmitBoardEntrySummary();
				void this.contextModeSyncQueue.then(() =>
					this.postAgentEvent('board_switched', {
						board,
						source: planStepHint?.source || 'manual_board_switch',
						isCollaborative,
						modeContextVersion: this.modeContextVersion,
						...(buildingBlockId ? { buildingBlockId } : {}),
						...(omitBoardSummary ? { omitBoardSummary: true } : {}),
					})
				);
				return;
			}
		}
		const payloadData: Record<string, unknown> =
			data && typeof data === 'object' && !Array.isArray(data)
				? { ...(data as Record<string, unknown>) }
				: { value: data };
		payloadData.isCollaborative = globalThis.neuroCreate.graph?.isCollaborative === true;
		payloadData.graphMode = globalThis.neuroCreate.graph?.graphMode;
		if (GENERATED_RESULT_EVENT_TYPES.has(eventType) || CONTEXT_CHANGING_TUTORIAL_EVENT_TYPES.has(eventType)) {
			payloadData.flowyContextVersion = this.boardInteractionVersion;
		}
		const primaryEvent = this.postAgentEvent('tutorial_event', { eventType, data: payloadData });
		if (
			(eventType === TUTORIAL_EVENT_TYPES.addedSingleNode ||
				eventType === TUTORIAL_EVENT_TYPES.addedTwoNodes) &&
			payloadData.graphMode === 'moodboard' &&
			payloadData.isCollaborative === false &&
			Boolean(this.lastSessionState?.activeSequenceId)
		) {
			const entries = [payloadData.node, payloadData.firstNode, payloadData.secondNode].filter(
				(entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object'
			);
			const savedNode = entries[0];
			const label = typeof savedNode?.label === 'string' ? savedNode.label.trim() : '';
			const nodeUid = typeof savedNode?.uid === 'string' ? savedNode.uid : '';
			void primaryEvent.finally(() => {
				const currentGraph = globalThis.neuroCreate.graph;
				// The user may have moved to Team Synthesise while the preceding
				// event was being sent. In that case the node is already shared.
				if (
					currentGraph?.graphMode !== 'moodboard' ||
					currentGraph.isCollaborative === true
				) {
					return;
				}
				return this.postAgentEvent('personal_board_result_saved', {
					labels: label ? [label] : [],
					board: 'moodboard',
					isPersonalBoard: true,
					...(nodeUid ? { nodeUid } : {}),
				});
			});
		}
		if (this.onboardingGoalPending) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'suppressed_during_board_bootstrap',
				eventType,
				data,
			});
			return;
		}
	}

	private shouldSuppressIdeateAnalysisClusterEvent(eventType: string): boolean {
		if (eventType !== TUTORIAL_EVENT_TYPES.addedCluster) return false;
		const activeBoard = globalThis.neuroCreate.graph?.graphMode || null;
		return activeBoard === 'ideate';
	}

	private scheduleBoardInitialization() {
		if (this.boardInitialized) return;
		if (this.boardInitializationTimer !== null) {
			window.clearTimeout(this.boardInitializationTimer);
		}
		this.boardInitializationTimer = window.setTimeout(async () => {
			if (this.boardInitialized) return;
			this.boardInitialized = true;
			this.logDebug('SYSTEM_EVENT', { message: 'board_initialized' });
			await this.requestSessionWelcome();
		}, 1200);
	}

	private getHashForBoard(board: FlowyPlanBoard, collaborative = false): string {
		if (board === 'inspire') return '#inspire';
		if (board === 'ideate') return collaborative ? '#ideate-team' : '#ideate';
		return collaborative ? '#synthesise-team' : '#synthesise';
	}

	private syncActivePlanStepForBoard(
		board: FlowyPlanBoard,
		preferred?: { stepId?: string; buildingBlockId?: string | null },
	) {
		if (!this.plan?.steps?.length) return;
		let match: AgentPlanStep | undefined;
		if (preferred?.stepId) {
			match = this.plan.steps.find((step) => step.id === preferred.stepId);
		}
		if (!match && preferred?.buildingBlockId) {
			match = this.plan.steps.find(
				(step) => step.board === board && step.buildingBlockId === preferred.buildingBlockId,
			);
		}
		if (!match) {
			// Step-level status is not mutated when a building block completes —
			// the backend tracks completion via `sessionState.completedBuildingBlockIds`.
			// Use that set (if known) to skip completed blocks; otherwise fall back
			// to the first step on the board.
			const completedIds = new Set(this.lastSessionState?.completedBuildingBlockIds || []);
			match =
				this.plan.steps.find(
					(step) =>
						step.board === board &&
						step.status !== 'completed' &&
						!(step.buildingBlockId && completedIds.has(step.buildingBlockId)),
				) || this.plan.steps.find((step) => step.board === board);
		}
		if (match) {
			this.activePlanStepId = match.id;
		}
	}

	private addNeutralEventMessage(content: string, options?: { persist?: boolean }) {
		const normalized = (content || '').trim();
		if (!normalized) return;
		const last = this.messages[this.messages.length - 1];
		if (last?.role === 'event' && last.content === normalized) return;
		this.messages = [...this.messages, { role: 'event', content: normalized }];
		this.logDebug('SYSTEM_EVENT', { content: normalized });
		if (options?.persist !== false) {
			void this.postAgentEvent('ui_event_toast', { content: normalized });
		}
		this.requestUpdate();
		this.scrollMessagesToBottom();
		this.resetIdleAssistTimer();
	}

	private queueNodeAddedStatus(data: unknown): void {
		const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
		const nodes = [payload.node, payload.firstNode, payload.secondNode].filter(
			(node): node is Record<string, unknown> => Boolean(node && typeof node === 'object')
		);
		for (const node of nodes) {
			const label =
				typeof node.label === 'string' && node.label.trim()
					? node.label
					: node.isVideo === true
						? 'Video'
						: typeof node.src === 'string' && node.src
							? 'Image'
							: 'Untitled node';
			this.pendingNodeAddedStatuses.push({
				label,
				pdfAnalysisId:
					typeof node.pdfAnalysisId === 'string' ? node.pdfAnalysisId : undefined,
			});
		}
		if (!nodes.length) {
			this.pendingNodeAddedStatuses.push({ label: 'Untitled node' });
		}
		if (this.nodeAddedStatusTimer !== null) {
			window.clearTimeout(this.nodeAddedStatusTimer);
		}
		this.nodeAddedStatusTimer = window.setTimeout(
			() => this.flushPendingNodeAddedStatuses(),
			NODE_ADDED_STATUS_DEBOUNCE_MS
		);
	}

	private flushPendingNodeAddedStatuses(): void {
		if (this.nodeAddedStatusTimer !== null) {
			window.clearTimeout(this.nodeAddedStatusTimer);
			this.nodeAddedStatusTimer = null;
		}
		const pending = this.pendingNodeAddedStatuses;
		this.pendingNodeAddedStatuses = [];
		if (pending.length) {
			this.addNeutralEventMessage(formatNodeAddedStatus(pending.map(({ label }) => label)));
		}
	}

	private discardPendingPdfNodeStatuses(analysisId: string): void {
		if (!analysisId) return;
		this.pendingNodeAddedStatuses = this.pendingNodeAddedStatuses.filter(
			(status) => status.pdfAnalysisId !== analysisId
		);
		if (!this.pendingNodeAddedStatuses.length && this.nodeAddedStatusTimer !== null) {
			window.clearTimeout(this.nodeAddedStatusTimer);
			this.nodeAddedStatusTimer = null;
		}
	}

	private parseJsonObject(value: string | undefined): Record<string, unknown> | null {
		if (!value) return null;
		try {
			const parsed = JSON.parse(value) as unknown;
			return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
		} catch {
			return null;
		}
	}

	private firstStringField(source: Record<string, unknown> | null, keys: string[]): string | null {
		if (!source) return null;
		for (const key of keys) {
			const value = source[key];
			if (typeof value === 'string' && value.trim()) {
				return value.trim();
			}
		}
		return null;
	}

	private formatToolActivityMessage(step: ToolStep | undefined, event: Extract<AgentEvent, { type: 'tool_result' }>) {
		const toolName = (step?.name || '').trim();
		if (!toolName && !step?.label) {
			return null;
		}
		const normalizedTool = toolName.toLowerCase();
		const args = this.parseJsonObject(step?.arguments);
		const output = this.parseJsonObject(event.output);

		if (normalizedTool.includes('findinterestingnode')) {
			const suggestedLabel =
				event.nodeRefs?.[0]?.label ||
				this.firstStringField(output, ['label', 'nodeLabel', 'node', 'suggestedNode']);
			if (suggestedLabel) {
				return `Suggested node on board: "${suggestedLabel}"`;
			}
			return 'Suggested node on board';
		}

		if (normalizedTool === 'search' || normalizedTool.endsWith('/search')) {
			const lenses = Array.isArray(output?.researchLenses)
				? (output.researchLenses as Array<{ label?: unknown }>)
				: [];
			const lensLabels = lenses
				.map((lens) => (typeof lens?.label === 'string' ? lens.label.trim() : ''))
				.filter(Boolean);
			if (lensLabels.length) {
				this.lastUsedResearchLensLabels = lensLabels;
				const subject = this.firstStringField(args, ['searchTerm', 'parentLabel', 'topic', 'label']);
				const resultCount = Array.isArray(output?.result) ? output.result.length : event.nodeRefs?.length || 0;
				const prefix = subject ? `Search (“${subject}”) across lenses:` : 'Search across lenses:';
				return `${prefix} ${lensLabels.join(', ')}${resultCount ? ` · ${resultCount} hit${resultCount === 1 ? '' : 's'}` : ''}`;
			}
		}

		const toolLabels: Record<string, string> = {
			spark: 'spark',
			empathise: 'empathise',
			brainstorm: 'brainstorm',
			commonideas: 'common ideas',
			search: 'search',
			persona: 'persona',
			personas: 'persona',
			generateimage: 'image',
			image: 'image',
		};
		const baseToolLabel = toolLabels[normalizedTool] || (step?.label || toolName || 'tool');
		const subject =
			this.firstStringField(args, [
				'label',
				'searchTerm',
				'parentLabel',
				'topic',
				'concept',
				'nodeLabel',
				'activeNode',
			]) || this.firstStringField(output, ['label', 'topic', 'searchTerm', 'activeNode']);
		return subject ? `Ran ${baseToolLabel} on "${subject}"` : `Ran ${baseToolLabel}`;
	}

	private addToolActivityMessage(content: string, options?: { persist?: boolean }) {
		const normalized = (content || '').trim();
		if (!normalized) return;
		const last = this.messages[this.messages.length - 1];
		if (last?.role === 'tool' && last.content === normalized) return;
		this.messages = [...this.messages, { role: 'tool', content: normalized }];
		this.logDebug('SYSTEM_EVENT', { toolActivity: normalized });
		if (options?.persist !== false) {
			void this.postAgentEvent('ui_tool_toast', { content: normalized });
		}
		this.requestUpdate();
		this.scrollMessagesToBottom();
		this.resetIdleAssistTimer();
	}

	private appendInFlightToolMessagesFromLiveState(toolSteps: ToolStep[] | null | undefined) {
		if (!Array.isArray(toolSteps) || !toolSteps.length) {
			return;
		}
		for (const step of toolSteps) {
			if (!step || step.status !== 'running') {
				continue;
			}
			if (this.announcedLiveToolCallIds.has(step.callId)) {
				continue;
			}
			this.announcedLiveToolCallIds.add(step.callId);
			const label = (step.label || step.name || 'tool').trim();
			this.addToolActivityMessage(`Running ${label}...`, { persist: false });
		}
	}

	private formatTutorialEventToast(eventType: string, data: unknown): string | null {
		const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
		if (eventType === TUTORIAL_EVENT_TYPES.likedNode) {
			const node = payload.node as Record<string, unknown> | undefined;
			const liked = Boolean(payload.liked);
			const verb = liked ? 'Added' : 'Removed';
			const preposition = liked ? 'to' : 'from';
			const rawLabel = typeof node?.label === 'string' ? node.label.trim() : '';
			const hasSrc = typeof node?.src === 'string' && (node.src as string).length > 0;
			const isVideo = Boolean(node?.isVideo);
			let subject = 'node';
			if (rawLabel) {
				const isSingleWord = !/\s/.test(rawLabel) && rawLabel.length > 0;
				subject = isSingleWord ? `**${rawLabel}**` : 'node';
			} else if (hasSrc) {
				subject = isVideo ? 'video' : 'image';
			}
			return `${verb} ${subject} ${preposition} the idea box`;
		}
		if (eventType === TUTORIAL_EVENT_TYPES.likedResult) {
			const liked = Boolean(payload.liked);
			const verb = liked ? 'Added' : 'Removed';
			const preposition = liked ? 'to' : 'from';
			const rawType = typeof payload.type === 'string' ? payload.type : '';
			const labelByType: Record<string, string> = {
				persona: 'persona',
				motivation: 'motivation',
				motivations: 'motivations',
				audience: 'audience',
				haiku: 'haiku',
				'ai-haiku': 'haiku',
				proverb: 'tagline',
				brainstorm: 'brainstorm idea',
				common: 'common idea',
				'image-inspire': 'image',
				'video-inspire': 'video',
				'image-synthesise': 'image',
				node: 'node',
				cluster: 'cluster',
				linkedinpost: 'LinkedIn post',
			};
			const subject = labelByType[rawType] || 'result';
			return `${verb} ${subject} ${preposition} the idea box`;
		}
		const statusEvents: Record<string, string> = {
			sparkResultsGenerated: 'Spark results generated',
			empathiseResultsGenerated: 'Empathise results generated',
			brainstormResultsGenerated: 'Brainstorm results generated',
			commonIdeasResultsGenerated: 'Common ideas generated',
			haikuResultsGenerated: 'Haiku results generated',
			aiHaikuResultsGenerated: 'AI haiku results generated',
			taglineResultsGenerated: 'Tagline results generated',
			searchResultsGenerated: 'Search results generated',
			personaResultsGenerated: 'Persona results generated',
			storyResultsGenerated: 'Story results generated',
			characterResultsGenerated: 'Character results generated',
			brandColoursResultsGenerated: 'Brand colours generated',
			brandToneResultsGenerated: 'Tone of voice generated',
			stakeholderMotivationsGenerated: 'Stakeholder motivations generated',
			imageInspireGenerated: 'Inspiration image generated',
			videoInspireGenerated: 'Inspiration video generated',
			imageSynthesiseGenerated: 'Synthesise image generated',
			audiencesGenerated: 'Audience suggestions generated',
		};
		return statusEvents[eventType] || null;
	}

	private getPlanStepLabelByBuildingBlockId(buildingBlockId: string): string | null {
		if (!this.plan?.steps?.length) return null;
		const match = this.plan.steps.find((step) => step.buildingBlockId === buildingBlockId);
		return match?.label || null;
	}

	private hasPlanSteps(): boolean {
		return Boolean(this.plan?.steps?.length);
	}

	/**
	 * A plan is drafted only after the user explicitly chooses or writes a
	 * board goal. Requiring both values also protects legacy sessions where
	 * onboarding context was previously stored as a goal before any choice.
	 */
	private canFlowyAutoSelectNodes(): boolean {
		return shouldAllowFlowyAutoSelectNodes({
			hasGoal: Boolean(this.getDisplaySessionGoal()),
			hasPlan: this.hasPlanSteps(),
		});
	}

	private hasAcceptedPlanLock(): boolean {
		return this.planAccepted || this.sharedPlanState?.sharedSession?.planAccepted === true;
	}

	private applyIncomingPlanAccepted(planAccepted: boolean | undefined) {
		if (this.hasAcceptedPlanLock()) {
			this.planAccepted = true;
			return;
		}
		if (planAccepted !== undefined) {
			this.planAccepted = planAccepted;
		}
	}

	/** Apply a plan parsed from proposePlan tool output or SSE/HTTP payloads. */
	private applyPlanFromPayload(payload: unknown): boolean {
		if (this.hasAcceptedPlanLock()) {
			return false;
		}
		if (!payload || typeof payload !== 'object') {
			return false;
		}
		const obj = payload as { plan?: AgentPlan };
		if (obj.plan?.steps?.length) {
			this.plan = obj.plan;
			this.syncPlanSidebarState({ expandWhenPending: true });
			return true;
		}
		return false;
	}

	private applyPlanFromToolOutput(output: string): boolean {
		let parsed: unknown;
		try {
			parsed = JSON.parse(output);
		} catch {
			return false;
		}
		if (typeof parsed === 'string') {
			try {
				parsed = JSON.parse(parsed);
			} catch {
				return false;
			}
		}
		return this.applyPlanFromPayload(parsed);
	}

	private planAccentStyle(colour?: string): string {
		return `--plan-accent: var(${colour || '--orange'});`;
	}

	/** Sync sidebar collapse with plan acceptance; optionally expand chat when plan is pending review. */
	private syncPlanSidebarState(options?: { expandWhenPending?: boolean }) {
		if (!this.hasPlanSteps()) return;
		if (this.sharedPlanState?.sharedSession?.planAccepted) {
			this.planAccepted = true;
		}
		const previousCollapsed = this.isPlanCollapsed;
		this.isPlanCollapsed = this.planAccepted;
		if (options?.expandWhenPending && !this.planAccepted) {
			this.isPlanCollapsed = false;
			this.isMinimized = false;
		}
		if (previousCollapsed !== this.isPlanCollapsed) {
			this.releasePanelDimensions();
			this.syncExpandedPanelRectWidth();
		}
	}

	/** Show the plan sidebar when restoring an existing session; it lives inside the minimized panel body. */
	private revealRestoredPlanSidebar() {
		if (!this.hasPlanSteps() || this.planAccepted) return;
		this.isMinimized = false;
		this.isPlanCollapsed = false;
		this.releasePanelDimensions();
		this.syncExpandedPanelRectWidth();
	}

	private applySessionStateToasts(sessionState: AgentSessionStateLite | null) {
		if (!sessionState) return;
		if (typeof sessionState.modeContextVersion === 'number') {
			this.modeContextVersion = Math.max(
				this.modeContextVersion,
				sessionState.modeContextVersion
			);
		}
		if (sessionState.activeBoard === 'ideate' && sessionState.ideateContextMode) {
			this.updateContextModeStatus(
				'ideate',
				sessionState.ideateContextMode === 'team'
			);
		} else if (
			sessionState.activeBoard === 'moodboard' &&
			sessionState.synthesiseContextMode
		) {
			this.updateContextModeStatus(
				'moodboard',
				sessionState.synthesiseContextMode === 'team'
			);
		} else if (sessionState.activeBoard === 'inspire') {
			this.updateContextModeStatus('inspire', true);
		}
		const previous = this.lastSessionState;
		if (
			sessionState.activeBuildingBlockId &&
			sessionState.activeBuildingBlockId !== previous?.activeBuildingBlockId &&
			!this.sharedPlanState?.sharedSession?.planAccepted
		) {
			const stepLabel =
				this.getPlanStepLabelByBuildingBlockId(sessionState.activeBuildingBlockId) ||
				sessionState.activeBuildingBlockId;
			this.addNeutralEventMessage(`Plan progression: ${stepLabel}`);
		}
		this.lastSessionState = sessionState;
		// Mirror the BT's chosen activeBuildingBlockId into the plan-strip
		// highlight. Without this, navigating to a board with completed early
		// blocks leaves the highlight on the first (already finished) step.
		// In a shared plan, prefer the shared/team step or the participant's
		// joined step; stale per-user BT state can lag behind host advancement.
		if (this.sharedPlanState?.sharedSession?.planAccepted) {
			return;
		}
		if (sessionState.activeBuildingBlockId && this.plan?.steps?.length) {
			const board = sessionState.activeBoard;
			const matchingStep = this.plan.steps.find(
				(step) =>
					step.buildingBlockId === sessionState.activeBuildingBlockId &&
					(!board || step.board === board),
			);
			if (matchingStep && matchingStep.id !== this.activePlanStepId) {
				this.activePlanStepId = matchingStep.id;
				this.requestUpdate();
			}
		}
	}

	private subscribe() {
		if (this.unsubscribe) {
			this.unsubscribe();
		}
		if (window.subscribeAgentSession) {
			this.unsubscribe = window.subscribeAgentSession(this.sessionId, (event) => {
				if (event.boardId !== this.boardId || event.sessionId !== this.sessionId) {
					return;
				}
				this.handleAgentEvent(event);
			});
		}
	}

	private toCssColour(colour?: string) {
		if (!colour) {
			return 'var(--grey4)';
		}
		return colour.startsWith('#') ? colour : `var(--${colour})`;
	}

	private escapeHtml(value: string) {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	private decodeHtmlEntities(value: string) {
		const textarea = document.createElement('textarea');
		textarea.innerHTML = value;
		return textarea.value;
	}

	private injectNodeReferences(content: string) {
		return content.replace(/\[\[node:([^|\]]+)\|([^|\]]+)\|([^|\]]*)(?:\|([^|\]]*))?\]\]/g, (_, uid, label, colour, imageUrl) => {
			const safeUid = this.escapeHtml(String(uid));
			const safeLabel = this.escapeHtml(
				truncateFlowyText(this.decodeHtmlEntities(String(label)))
			);
			const cssColour = this.escapeHtml(this.toCssColour(String(colour || '').trim()));
			const safeImageUrl = typeof imageUrl === 'string' && imageUrl.trim()
				? this.escapeHtml(imageUrl.trim())
				: '';
			const preview = safeImageUrl
				? `<span class="agent-chat-panel__node-link-preview" style="background-image:url('${safeImageUrl}')"></span>`
				: '';
			return `<button type="button" class="agent-chat-panel__node-link" data-node-uid="${safeUid}" style="--node-accent:${cssColour}">${preview}<span>${safeLabel}</span></button>`;
		});
	}

	private focusNodeById(nodeUid: string) {
		this.cancelPendingBoardCenter();
		const graph = globalThis.neuroCreate.graph as
			| (typeof globalThis.neuroCreate.graph & {
					focusNodesIfNeeded?: (nodeIds: string[], alwaysCenter?: boolean) => void;
			  })
			| undefined;
		const node = graph?.getNode(nodeUid);
		if (!graph || !node) {
			return false;
		}

		// Prefer the graph's own focus helper; fallback to active+center.
		if (typeof graph.focusNodesIfNeeded === 'function') {
			graph.focusNodesIfNeeded([nodeUid], true);
		} else {
			graph.updateActive(node, true, false, false, true);
			graph.centerTo(node);
		}
		return true;
	}

	private cancelPendingBoardCenter() {
		if (this.pendingBoardCenterFrame === null) return;
		window.cancelAnimationFrame(this.pendingBoardCenterFrame);
		this.pendingBoardCenterFrame = null;
	}

	private centerBoardAfterPlanNavigation() {
		this.cancelPendingBoardCenter();
		const graph = globalThis.neuroCreate.graph;
		if (!graph) return;

		// Wait until the newly loaded board has completed its first layout frame.
		this.pendingBoardCenterFrame = window.requestAnimationFrame(() => {
			this.pendingBoardCenterFrame = null;
			if (globalThis.neuroCreate.graph !== graph) return;
			graph.centerOnCentralPoint();
		});
	}

	private applyBackendFlowyEffects(effects: FlowyEffect[] | null | undefined) {
		if (!Array.isArray(effects) || !effects.length) return;
		for (const effect of effects) {
			if (!effect || effect.type === 'assistant_message') continue;
			if (effect.type === 'highlight_node') {
				if (!this.canFlowyAutoSelectNodes()) continue;
				const highlightEffect = effect as { nodeId?: unknown };
				if (typeof highlightEffect.nodeId === 'string') {
					if (searchUi.shouldPreserveSearchSourceSelection(highlightEffect.nodeId)) continue;
					this.focusNodeById(highlightEffect.nodeId);
				}
				continue;
			}
			if (effect.type === 'open_tool_panel') {
				const panelEffect = effect as {
					panel?: unknown;
					task?: unknown;
					input?: unknown;
					selectedNodeIds?: unknown;
					selectedNodeLabels?: unknown;
				};
				const task = typeof panelEffect.task === 'string' ? panelEffect.task : null;
				const panelInput =
					panelEffect.input && typeof panelEffect.input === 'object'
						? (panelEffect.input as Record<string, unknown>)
						: {};
				const selectedNodeIds = Array.isArray(panelEffect.selectedNodeIds)
					? panelEffect.selectedNodeIds.filter((value): value is string => typeof value === 'string')
					: undefined;
				const selectedNodeLabels = Array.isArray(panelEffect.selectedNodeLabels)
					? panelEffect.selectedNodeLabels.filter((value): value is string => typeof value === 'string')
					: undefined;
				if (panelEffect.panel === 'generate') {
					generateUi.openBackendGeneratePanel({
						task,
						tool: task,
						selectedNodeIds,
						selectedNodeLabels,
						showLoading: panelInput.source === 'agent_tool_call',
					});
				}
				if (panelEffect.panel === 'search') {
					searchUi.openBackendSearchPanel({
						selectedNodeIds,
						selectedNodeLabels,
					});
				}
				if (panelEffect.panel === 'spark') {
					sparkUi.openBackendSparkPanel({
						selectedNodeIds,
						selectedNodeLabels,
					});
				}
				if (panelEffect.panel === 'empathise') {
					empathiseUi.openBackendEmpathisePanel({
						selectedNodeIds,
						selectedNodeLabels,
					});
				}
				continue;
			}
			if (effect.type === 'show_tool_results') {
				const resultEffect = effect as {
					panel?: unknown;
					tool?: unknown;
					task?: unknown;
					results?: unknown;
					selectedNodeIds?: unknown;
					selectedNodeLabels?: unknown;
					recommendedResultId?: unknown;
					recommendedResultIds?: unknown;
					removedSensitiveCount?: unknown;
					sensitivityExplanation?: unknown;
					availableLenses?: unknown;
					selectedLensId?: unknown;
					sourceDiagnostics?: unknown;
				};
				const task = typeof resultEffect.task === 'string' ? resultEffect.task : null;
				const tool = typeof resultEffect.tool === 'string' ? resultEffect.tool : '';
				const selectedNodeIds = Array.isArray(resultEffect.selectedNodeIds)
					? resultEffect.selectedNodeIds.filter((value): value is string => typeof value === 'string')
					: undefined;
				const selectedNodeLabels = Array.isArray(resultEffect.selectedNodeLabels)
					? resultEffect.selectedNodeLabels.filter((value): value is string => typeof value === 'string')
					: undefined;
				const recommendedResultId =
					typeof resultEffect.recommendedResultId === 'string' ? resultEffect.recommendedResultId : null;
				const recommendedResultIds = Array.isArray(resultEffect.recommendedResultIds)
					? resultEffect.recommendedResultIds.filter(
							(value): value is string => typeof value === 'string' && value.trim().length > 0
						)
					: undefined;
				if (resultEffect.panel === 'generate') {
					const rawResults = Array.isArray(resultEffect.results) ? resultEffect.results : [];
					const renderedResults = generateUi.showBackendGenerateResults({
						task,
						tool,
						results: rawResults as Array<{
							id?: string;
							type?: 'article' | 'image';
							title?: string;
							snippet?: string;
							label?: string;
							url?: string;
							source?: string;
							imageUrl?: string;
							videoUrl?: string;
						}>,
						selectedNodeIds,
						selectedNodeLabels,
						recommendedResultId,
						recommendedResultIds,
					});
					if (renderedResults) {
						// Replacing the panel invalidates evaluations of any earlier result set.
						this.boardInteractionVersion += 1;
						this.emitBackendPanelResultsGenerated({
							task,
							tool,
							results: renderedResults,
							selectedNodeLabels,
						});
					}
				}
				if (resultEffect.panel === 'search') {
					const rawResults = Array.isArray(resultEffect.results) ? resultEffect.results : [];
					searchUi.showBackendSearchResults({
						results: rawResults as Array<{
							id?: string;
							type?: 'article' | 'image';
							title?: string;
							snippet?: string;
							label?: string;
							url?: string;
							source?: string;
							platform?: string;
							imageUrl?: string;
							lensId?: string;
							lensLabel?: string;
						}>,
						selectedNodeIds,
						selectedNodeLabels,
						recommendedResultId,
						recommendedResultIds,
						removedSensitiveCount:
							typeof resultEffect.removedSensitiveCount === 'number'
								? resultEffect.removedSensitiveCount
								: undefined,
						sensitivityExplanation:
							typeof resultEffect.sensitivityExplanation === 'string'
								? resultEffect.sensitivityExplanation
								: undefined,
						availableLenses: Array.isArray(resultEffect.availableLenses)
							? (resultEffect.availableLenses as Array<{
									id: string;
									label: string;
									purpose: string;
									preferredSources: string[];
								}>)
							: undefined,
						selectedLensId:
							typeof resultEffect.selectedLensId === 'string'
								? resultEffect.selectedLensId
								: undefined,
						sourceDiagnostics: Array.isArray(resultEffect.sourceDiagnostics)
							? (resultEffect.sourceDiagnostics as Array<{
									source: string;
									status: 'ok' | 'empty' | 'failed';
									entries: number;
									error?: string;
								}>)
							: undefined,
					});
				}
				if (resultEffect.panel === 'spark') {
					const rawResults = Array.isArray(resultEffect.results) ? resultEffect.results : [];
					sparkUi.showBackendSparkResults({
						results: rawResults as Array<{
							id?: string;
							title?: string;
							snippet?: string;
							label?: string;
							topics?: string[];
						}>,
						selectedNodeIds,
						selectedNodeLabels,
						recommendedResultId,
						recommendedResultIds,
					});
				}
				if (resultEffect.panel === 'empathise') {
					const rawResults = Array.isArray(resultEffect.results) ? resultEffect.results : [];
					empathiseUi.showBackendEmpathiseResults({
						results: rawResults as Array<{
							id?: string;
							title?: string;
							snippet?: string;
							label?: string;
							colour?: string;
							color?: string;
							topics?: string[];
						}>,
						selectedNodeIds,
						selectedNodeLabels,
						recommendedResultId,
						recommendedResultIds,
					});
				}
				continue;
			}
			if (effect.type === 'highlight_result') {
				const highlightEffect = effect as {
					tool?: unknown;
					resultId?: unknown;
					resultIds?: unknown;
					resultIndex?: unknown;
					label?: unknown;
				};
				const tool = typeof highlightEffect.tool === 'string' ? highlightEffect.tool : '';
				const resultIds = Array.isArray(highlightEffect.resultIds)
					? highlightEffect.resultIds.filter(
							(value): value is string => typeof value === 'string' && value.trim().length > 0
						)
					: null;
				const resultIndex =
					typeof highlightEffect.resultIndex === 'number' ? highlightEffect.resultIndex : null;
				if (tool === 'search') {
					searchUi.highlightSearchResult({
						resultId: typeof highlightEffect.resultId === 'string' ? highlightEffect.resultId : null,
						resultIds,
						resultIndex,
						label: typeof highlightEffect.label === 'string' ? highlightEffect.label : null,
					});
					continue;
				}
				if (tool === 'spark') {
					sparkUi.highlightSparkResult({
						resultId: typeof highlightEffect.resultId === 'string' ? highlightEffect.resultId : null,
						resultIds,
						resultIndex,
						label: typeof highlightEffect.label === 'string' ? highlightEffect.label : null,
					});
					continue;
				}
				if (tool === 'empathise') {
					empathiseUi.highlightEmpathiseResult({
						resultId: typeof highlightEffect.resultId === 'string' ? highlightEffect.resultId : null,
						resultIds,
						resultIndex,
						label: typeof highlightEffect.label === 'string' ? highlightEffect.label : null,
					});
					continue;
				}
				generateUi.highlightGenerateResult({
					resultId: typeof highlightEffect.resultId === 'string' ? highlightEffect.resultId : null,
					resultIds,
					resultIndex,
					label: typeof highlightEffect.label === 'string' ? highlightEffect.label : null,
				});
				continue;
			}
			if (effect.type === 'select_ideate_insight') {
				const ideateEffect = effect as { insight?: unknown };
				if (typeof ideateEffect.insight === 'string') {
					const selector = () =>
						(globalThis as unknown as { flowySelectIdeateInsight?: (insightName: string) => boolean })
							.flowySelectIdeateInsight?.(ideateEffect.insight as string);
					if (!selector()) {
						window.setTimeout(selector, 100);
					}
				}
			}
		}
	}

	private compactSessionGoal(goal: string): string {
		const normalized = goal.replace(/^goal:\s*/i, '').trim();
		if (!normalized) return '';
		const prefix = normalized.split(':')[0]?.trim() || '';
		if (prefix && prefix.length >= 8 && prefix.length <= 64 && normalized.includes(':')) {
			return prefix;
		}
		if (/^get inspired and explore the topic$/i.test(normalized)) {
			return 'Get inspired / explore';
		}
		return normalized;
	}

	private syncSessionGoal(goal?: string | null) {
		const normalized = typeof goal === 'string' ? goal.trim() : '';
		if (normalized) {
			this.sessionGoal = this.pendingSessionGoalLabel || this.compactSessionGoal(normalized);
			this.pendingSessionGoalLabel = null;
			if (this.onboardingGoalPending) {
				this.onboardingGoalPending = false;
				this.deferredBootstrapTutorialPending = true;
			}
		}
	}

	private startDeferredBootstrapTutorial(): void {
		if (!this.deferredBootstrapTutorialPending) return;
		this.deferredBootstrapTutorialPending = false;
		// The mini-tutorial remains part of onboarding, but it now follows the
		// user's first goal choice rather than interrupting the opening question.
		window.setTimeout(() => {
			tutorialEmitter.emit(TUTORIAL_EVENT_TYPES.afterAutoSpark, { nodes: [] });
		}, 250);
	}

	private emitBackendPanelResultsGenerated({
		task,
		tool,
		results,
		selectedNodeLabels,
	}: {
		task: string | null;
		tool: string;
		results: unknown[];
		selectedNodeLabels?: string[];
	}) {
		const raw = (task || tool || '').trim().toLowerCase();
		const eventByTask: Record<string, string> = {
			brainstorm: TUTORIAL_EVENT_TYPES.brainstormResultsGenerated,
			brainstormsequence: TUTORIAL_EVENT_TYPES.brainstormResultsGenerated,
			common: TUTORIAL_EVENT_TYPES.commonIdeasResultsGenerated,
			commonideas: TUTORIAL_EVENT_TYPES.commonIdeasResultsGenerated,
			commonideassequence: TUTORIAL_EVENT_TYPES.commonIdeasResultsGenerated,
			persona: TUTORIAL_EVENT_TYPES.personaResultsGenerated,
			personas: TUTORIAL_EVENT_TYPES.personaResultsGenerated,
			themespersonassequence: TUTORIAL_EVENT_TYPES.personaResultsGenerated,
			personalitypersonassequence: TUTORIAL_EVENT_TYPES.personaResultsGenerated,
			motivation: TUTORIAL_EVENT_TYPES.stakeholderMotivationsGenerated,
			motivations: TUTORIAL_EVENT_TYPES.stakeholderMotivationsGenerated,
			themesmotivationssequence: TUTORIAL_EVENT_TYPES.stakeholderMotivationsGenerated,
			tagline: TUTORIAL_EVENT_TYPES.taglineResultsGenerated,
			taglines: TUTORIAL_EVENT_TYPES.taglineResultsGenerated,
			proverb: TUTORIAL_EVENT_TYPES.taglineResultsGenerated,
			taglinessequence: TUTORIAL_EVENT_TYPES.taglineResultsGenerated,
			story: TUTORIAL_EVENT_TYPES.storyResultsGenerated,
			stories: TUTORIAL_EVENT_TYPES.storyResultsGenerated,
			character: TUTORIAL_EVENT_TYPES.characterResultsGenerated,
			characters: TUTORIAL_EVENT_TYPES.characterResultsGenerated,
			'brand-colours': TUTORIAL_EVENT_TYPES.brandColoursResultsGenerated,
			brandcolours: TUTORIAL_EVENT_TYPES.brandColoursResultsGenerated,
			brandcolors: TUTORIAL_EVENT_TYPES.brandColoursResultsGenerated,
			'brand-tone': TUTORIAL_EVENT_TYPES.brandToneResultsGenerated,
			brandtone: TUTORIAL_EVENT_TYPES.brandToneResultsGenerated,
			'tone-of-voice': TUTORIAL_EVENT_TYPES.brandToneResultsGenerated,
			haiku: TUTORIAL_EVENT_TYPES.haikuResultsGenerated,
			haikus: TUTORIAL_EVENT_TYPES.haikuResultsGenerated,
			haikussequence: TUTORIAL_EVENT_TYPES.haikuResultsGenerated,
			'ai-haiku': TUTORIAL_EVENT_TYPES.aiHaikuResultsGenerated,
			aihaiku: TUTORIAL_EVENT_TYPES.aiHaikuResultsGenerated,
			aihaikus: TUTORIAL_EVENT_TYPES.aiHaikuResultsGenerated,
			aihaikussequence: TUTORIAL_EVENT_TYPES.aiHaikuResultsGenerated,
			image: TUTORIAL_EVENT_TYPES.imageInspireGenerated,
			imageinspire: TUTORIAL_EVENT_TYPES.imageInspireGenerated,
			'image-inspire': TUTORIAL_EVENT_TYPES.imageInspireGenerated,
			video: TUTORIAL_EVENT_TYPES.videoInspireGenerated,
			videoinspire: TUTORIAL_EVENT_TYPES.videoInspireGenerated,
			'video-inspire': TUTORIAL_EVENT_TYPES.videoInspireGenerated,
		};
		const eventType = eventByTask[raw];
		if (!eventType || !results.length) return;
		const activeNode = selectedNodeLabels?.[0] || '';
		void this.postAgentEvent('tutorial_event', {
			eventType,
			data: {
				results,
				activeNode,
				flowyContextVersion: this.boardInteractionVersion,
				source: 'backend_panel_results',
			},
		});
	}

	private async postAgentEvent(
		type: string,
		payload?: Record<string, unknown>,
		requestModeContextVersion = this.modeContextVersion
	) {
		if (!this.boardId || !this.sessionId) {
			return;
		}
		if (type === 'idle_check') {
			const graph = globalThis.neuroCreate.graph;
			payload = {
				...(payload || {}),
				board: graph?.graphMode || this.lastSessionState?.activeBoard || 'inspire',
				isCollaborative: graph?.isCollaborative === true,
				modeContextVersion: requestModeContextVersion,
			};
		}
		this.logDebug('SYSTEM_EVENT', {
			message: 'post_agent_event',
			type,
			payload: payload || {},
		});
		if (type !== 'idle_check') {
			this.lastNonIdleEventAt = Date.now();
			this.idleTimerToken += 1;
			this.idleAssistStage = 0;
		}
		const tool = typeof payload?.tool === 'string' ? payload.tool : '';
		const tutorialEventType =
			type === 'tutorial_event' && typeof payload?.eventType === 'string' ? payload.eventType : '';
		const suggestionTools = new Set([
			'spark',
			'empathise',
			'brainstorm',
			'commonIdeas',
			'search',
			'personas',
			'image',
			'taglines',
			'haikus',
			'aiHaikus',
			'SparkSequence',
			'EmpathiseSequence',
			'BrainstormSequence',
			'CommonIdeasSequence',
			'SearchSequence',
			'PersonalityPersonasSequence',
			'AudienceProfileSequence',
			'ImageGenerationSequence',
			'TaglinesSequence',
			'HaikusSequence',
			'AiHaikusSequence',
		]);
		const toolLabels: Record<string, string> = {
			spark: 'spark',
			empathise: 'empathise',
			brainstorm: 'brainstorm',
			commonIdeas: 'common ideas',
			search: 'search',
			personas: 'persona',
			image: 'image',
			tagliens: 'tagline',
			taglines: 'tagline',
			haikus: 'haiku',
			aiHaikus: 'AI haiku',
			SparkSequence: 'spark',
			EmpathiseSequence: 'empathise',
			BrainstormSequence: 'brainstorm',
			CommonIdeasSequence: 'common ideas',
			SearchSequence: 'search',
			PersonalityPersonasSequence: 'persona',
			AudienceProfileSequence: 'audience',
			ImageGenerationSequence: 'image',
			TaglinesSequence: 'tagline',
			HaikusSequence: 'haiku',
			AiHaikusSequence: 'AI haiku',
		};
		const tutorialEventToolLabels: Record<string, string> = {
			sparkResultsGenerated: 'spark',
			empathiseResultsGenerated: 'empathise',
			brainstormResultsGenerated: 'brainstorm',
			commonIdeasResultsGenerated: 'common ideas',
			haikuResultsGenerated: 'haiku',
			aiHaikuResultsGenerated: 'AI haiku',
			taglineResultsGenerated: 'tagline',
			storyResultsGenerated: 'story',
			characterResultsGenerated: 'character',
			brandColoursResultsGenerated: 'brand colours',
			searchResultsGenerated: 'search',
			personaResultsGenerated: 'persona',
			imageInspireGenerated: 'image',
			videoInspireGenerated: 'video',
			imageSynthesiseGenerated: 'image',
			audiencesGenerated: 'audience',
		};
		const shouldShowSuggestionEvalStatus =
			(type === 'tool_started' || type === 'tool_finished') && suggestionTools.has(tool);
		const shouldShowTutorialEvalStatus =
			type === 'tutorial_event' && Boolean(tutorialEventType && tutorialEventToolLabels[tutorialEventType]);
		const shouldShowIdleThinking = type === 'idle_check';
		const payloadData =
			payload?.data && typeof payload.data === 'object'
				? (payload.data as Record<string, unknown>)
				: null;
		const resultContextVersion =
			typeof payloadData?.flowyContextVersion === 'number'
				? payloadData.flowyContextVersion
				: null;
		if (shouldShowSuggestionEvalStatus || shouldShowTutorialEvalStatus || shouldShowIdleThinking) {
			const toolLabel = shouldShowSuggestionEvalStatus
				? toolLabels[tool] || 'tool'
				: tutorialEventToolLabels[tutorialEventType] || 'tool';
			this.workingLabel = shouldShowIdleThinking ? 'Thinking...' : `Evaluating ${toolLabel} results...`;
			this.requestUpdate();
		}
		const response = await fetch(`/board/${this.boardId}/agent/event`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				sessionId: this.sessionId,
				type,
				payload: payload || {},
			}),
		}).catch(() => null);
		if (!response?.ok) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'post_agent_event_failed',
				type,
				status: response?.status || null,
			});
			if (type === 'idle_check' && this.workingLabel === 'Thinking...') {
				this.workingLabel = '';
				this.requestUpdate();
			}
			return;
		}
		const data = (await response.json().catch(() => null)) as {
			message?: string | null;
			sessionState?: AgentSessionStateLite | null;
			toolMessages?: string[];
			nextStepHint?: NextStepHint | null;
			quickReplies?: AgentQuickReply[];
			effects?: FlowyEffect[];
		} | null;
		if (requestModeContextVersion !== this.modeContextVersion) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'discarded_stale_context_response',
				type,
				requestModeContextVersion,
				activeModeContextVersion: this.modeContextVersion,
			});
			if (type === 'idle_check' && this.workingLabel === 'Thinking...') {
				this.workingLabel = '';
				this.requestUpdate();
			}
			return;
		}
		this.applySessionStateToasts((data?.sessionState as AgentSessionStateLite | null) || null);
		const assistantEffect = data?.effects?.find(
			(effect): effect is Extract<FlowyEffect, { type: 'assistant_message' }> =>
				effect.type === 'assistant_message' && typeof effect.message === 'string'
		);
		const message = (data?.message || assistantEffect?.message || '').trim();
		const nextStepHint = data?.nextStepHint || assistantEffect?.nextStepHint || null;
		const quickReplies = data?.quickReplies?.length
			? data.quickReplies
			: assistantEffect?.quickReplies || [];
		this.logDebug('SYSTEM_EVENT', {
			message: 'post_agent_event_response',
			type,
			hasAssistantMessage: Boolean(message),
			sessionState: data?.sessionState || null,
			toolMessages: data?.toolMessages || [],
			nextStepHint,
			quickReplies,
			effects: data?.effects || [],
		});
		if (
			resultContextVersion !== null &&
			resultContextVersion < this.boardInteractionVersion &&
			message
		) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'discarded_stale_result_evaluation',
				tutorialEventType,
				resultContextVersion,
				boardInteractionVersion: this.boardInteractionVersion,
			});
			this.workingLabel = '';
			this.requestUpdate();
			return;
		}
		if (
			shouldShowIdleThinking &&
			message &&
			message === this.lastIdleMessage &&
			Date.now() - this.lastIdleMessageAt < IDLE_ASSIST_MESSAGE_COOLDOWN_MS
		) {
			this.workingLabel = '';
			this.requestUpdate();
			return;
		}
		this.applyBackendFlowyEffects(data?.effects || []);
		if (message) {
			this.logDebug('ASSISTANT_MESSAGE', {
				source: 'orchestrator_event',
				message,
				nextStepHint,
				quickReplies,
			});
			await this.streamAssistantMessage(
				message,
				nextStepHint,
				quickReplies,
				requestModeContextVersion
			);
			if (shouldShowIdleThinking) {
				this.lastIdleMessage = message;
				this.lastIdleMessageAt = Date.now();
			}
		}
		const toolMessages = Array.isArray(data?.toolMessages) ? data?.toolMessages : [];
		for (const rawMessage of toolMessages) {
			const toolMessage = typeof rawMessage === 'string' ? rawMessage.trim() : '';
			if (toolMessage) {
				this.addToolActivityMessage(toolMessage, { persist: false });
			}
		}
		if (message) {
			this.workingLabel = '';
			return;
		}
		if (shouldShowSuggestionEvalStatus || shouldShowTutorialEvalStatus || shouldShowIdleThinking) {
			this.workingLabel = '';
			this.requestUpdate();
		}
	}

	private shouldRunIdleCheck(): boolean {
		if (!this.boardInitialized) return false;
		if (!this.sessionId || !this.boardId) return false;
		if (this.isBusy || this.streamingAssistantContent) return false;
		if (this.hasPendingQuickReply()) return false;
		if ((this.workingLabel || '').trim().length > 0) return false;
		if (this.toolSteps.length > 0) return false;
		if (this.idleCheckInFlight) return false;
		if (Date.now() - this.lastNonIdleEventAt < IDLE_ASSIST_RECENT_EVENT_GUARD_MS) return false;
		return true;
	}

	private resetIdleAssistTimer() {
		this.idleTimerToken += 1;
		if (this.idleAssistTimer !== null) {
			window.clearTimeout(this.idleAssistTimer);
			this.idleAssistTimer = null;
		}
		if (this.hasPendingQuickReply()) {
			return;
		}
		if (this.idleAssistStage >= IDLE_ASSIST_CHECK_SCHEDULE_MS.length) {
			return;
		}
		const token = this.idleTimerToken;
		this.idleAssistTimer = window.setTimeout(() => {
			void this.runIdleAssistCheck(token);
		}, IDLE_ASSIST_CHECK_SCHEDULE_MS[this.idleAssistStage]);
	}

	private async runIdleAssistCheck(token: number) {
		const modeContextVersion = this.modeContextVersion;
		if (token !== this.idleTimerToken) {
			return;
		}
		if (!this.shouldRunIdleCheck()) {
			this.resetIdleAssistTimer();
			return;
		}
		this.idleCheckInFlight = true;
		try {
			await this.postAgentEvent(
				'idle_check',
				{ source: 'inactivity_timer' },
				modeContextVersion
			);
		} finally {
			if (
				token === this.idleTimerToken &&
				modeContextVersion === this.modeContextVersion
			) {
				this.idleCheckInFlight = false;
				this.idleAssistStage = Math.min(
					this.idleAssistStage + 1,
					IDLE_ASSIST_CHECK_SCHEDULE_MS.length
				);
				this.resetIdleAssistTimer();
			}
		}
	}

	private async streamAssistantMessage(
		content: string,
		nextStepHint?: NextStepHint | null,
		quickReplies: AgentQuickReply[] = [],
		modeContextVersion = this.modeContextVersion
	) {
		const chunks = content.match(/.{1,24}(\s|$)|\S+/g) || [content];
		this.streamingAssistantContent = '';
		this.requestUpdate();
		this.scrollMessagesToBottom();
		for (const chunk of chunks) {
			if (modeContextVersion !== this.modeContextVersion) {
				this.streamingAssistantContent = '';
				this.requestUpdate();
				return;
			}
			this.streamingAssistantContent = `${this.streamingAssistantContent}${chunk}`;
			this.requestUpdate();
			this.scrollMessagesToBottom();
			// eslint-disable-next-line no-await-in-loop
			await new Promise<void>((resolve) => window.setTimeout(resolve, 24));
		}
		if (modeContextVersion !== this.modeContextVersion) return;
		this.streamingAssistantContent = '';
		this.messages = [
			...this.messages,
			{
				role: 'assistant',
				content,
				nextStepHint: nextStepHint || undefined,
				quickReplies: quickReplies.length ? quickReplies : undefined,
			},
		];
		this.requestUpdate();
		this.scrollMessagesToBottom();
		this.resetIdleAssistTimer();
		this.focusNodeMentionedByFlowy(content);
	}

	private focusNodeMentionedByFlowy(
		content: string,
		explicitNodeRefs: AgentNodeReference[] = []
	) {
		if (!this.canFlowyAutoSelectNodes()) return;
		if (this.pendingNodeFocusTimer !== null) {
			window.clearTimeout(this.pendingNodeFocusTimer);
			this.pendingNodeFocusTimer = null;
		}

		let attempts = 0;
		const tryFocus = () => {
			this.pendingNodeFocusTimer = null;
			attempts += 1;
			const graph = globalThis.neuroCreate.graph as
				| (typeof globalThis.neuroCreate.graph & {
						focusNodesIfNeeded?: (nodeIds: string[], alwaysCenter?: boolean) => void;
				  })
				| undefined;
			const mentionedNodeIds = findMentionedBoardNodeIds(
				content,
				graph?.nodes || [],
				explicitNodeRefs.map((nodeRef) => nodeRef.uid)
			);
			const primaryNodeId = mentionedNodeIds[0];
			const primaryNode = primaryNodeId ? graph?.getNode(primaryNodeId) : undefined;
			if (graph && primaryNode) {
				this.cancelPendingBoardCenter();
				if (typeof graph.focusNodesIfNeeded === 'function') {
					graph.focusNodesIfNeeded([primaryNodeId], true);
				} else {
					graph.updateActive(primaryNode, true, false, false, true);
					graph.centerTo(primaryNode);
				}
				return;
			}
			if (attempts < 12) {
				this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
			}
		};

		tryFocus();
	}

	private focusNodesIfNeeded(nodeRefs: AgentNodeReference[], alwaysCenter = false) {
		if (!this.canFlowyAutoSelectNodes()) return;
		if (!nodeRefs.length) {
			return;
		}

		this.cancelPendingBoardCenter();
		if (this.pendingNodeFocusTimer !== null) {
			window.clearTimeout(this.pendingNodeFocusTimer);
			this.pendingNodeFocusTimer = null;
		}

		const nodeIds = nodeRefs.map((nodeRef) => nodeRef.uid);
		let attempts = 0;
		const tryFocus = () => {
			this.pendingNodeFocusTimer = null;
			attempts += 1;
			const graph = globalThis.neuroCreate.graph as
				| (typeof globalThis.neuroCreate.graph & {
						focusNodesIfNeeded?: (nodeIds: string[], forceCenter?: boolean) => void;
				  })
				| undefined;
			if (!graph) {
				if (attempts < 12) {
					this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
				}
				return;
			}
			const availableIds = nodeIds.filter((nodeId) => graph.getNode?.(nodeId));
			if (availableIds.length) {
				graph.focusNodesIfNeeded?.(availableIds, alwaysCenter);
				return;
			}
			if (attempts < 12) {
				this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
			}
		};

		this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
	}

	private focusMaterializedNodes(nodeIds: string[]) {
		if (!this.canFlowyAutoSelectNodes() || !nodeIds.length) return;
		this.cancelPendingBoardCenter();
		let attempts = 0;
		const tryFocus = () => {
			attempts += 1;
			const graph = globalThis.neuroCreate.graph as
				| (typeof globalThis.neuroCreate.graph & {
						focusMaterializedNodes?: (ids: string[]) => void;
					})
				| undefined;
			const availableIds = nodeIds.filter((nodeId) => graph?.getNode?.(nodeId));
			// ShareDB can deliver the seed before the materialised children. Wait
			// until the complete branch is present so the frame uses its true centre.
			if (graph && availableIds.length === nodeIds.length) {
				graph.focusMaterializedNodes?.(availableIds);
				return;
			}
			if (attempts < 12) {
				this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
			}
		};
		this.pendingNodeFocusTimer = window.setTimeout(tryFocus, 220);
	}

	private isSharedPlanHost(): boolean {
		return this.sharedPlanState?.participantState?.role === 'host';
	}

	private canEditPlan(): boolean {
		if (this.sharedPlanState?.sharedSession) {
			return this.isSharedPlanHost();
		}
		return document.body.dataset.boardOwner === 'true';
	}

	private getSharedActiveStep(): AgentPlanStep | null {
		const activeStepId = this.sharedPlanState?.sharedSession?.activeStepId || null;
		if (!activeStepId || !this.plan?.steps?.length) return null;
		return this.plan.steps.find((step) => step.id === activeStepId) || null;
	}

	private syncSharedPlanState(sharedPlanState: SharedPlanState | null) {
		const previousActiveStepId = this.sharedPlanState?.sharedSession?.activeStepId || null;
		this.sharedPlanState = sharedPlanState;
		this.sharedLeaveReported = false;
		this.lastSharedActivityId =
			this.lastSharedActivityId || sharedPlanState?.participantState?.lastSeenActivityId || null;
		const sharedPlan = sharedPlanState?.sharedSession?.plan || null;
		if (sharedPlan?.steps?.length) {
			this.plan = sharedPlan;
		}
		this.syncSessionGoal(sharedPlanState?.sharedSession?.goal || null);
		this.applyIncomingPlanAccepted(sharedPlanState?.sharedSession?.planAccepted);
		if (sharedPlanState?.sharedSession?.planAccepted) {
			this.isMinimized = false;
		}
		const activeStep = this.getSharedActiveStep();
		const participantJoinedStep =
			!this.isSharedPlanHost() && sharedPlanState?.participantState?.joinedStepId && this.plan?.steps?.length
				? this.plan.steps.find((step) => step.id === sharedPlanState.participantState?.joinedStepId) || null
				: null;
		const localActiveStep = participantJoinedStep || activeStep;
		if (localActiveStep) {
			this.activePlanStepId = localActiveStep.id;
		}
		if (
			activeStep?.id &&
			previousActiveStepId &&
			activeStep.id !== previousActiveStepId &&
			sharedPlanState?.participantState?.role !== 'host'
		) {
			this.isMinimized = false;
			this.isPlanCollapsed = false;
		}
		if (sharedPlanState?.sharedSession) {
			void this.ensureSharedActivityPolling();
		} else {
			this.stopSharedActivityPolling();
		}
	}

	private async loadSharedPlanState({ autoCatchup = false }: { autoCatchup?: boolean } = {}) {
		if (!this.boardId || !this.sessionId) return;
		if (autoCatchup) {
			// Hold board-entry recaps until catch-up finishes (including deferred join retries).
			this.omitBoardEntrySummary = true;
		}
		const response = await fetch(
			`/board/${this.boardId}/agent/shared-plan?sessionId=${encodeURIComponent(this.sessionId)}`
		).catch(() => null);
		if (!response?.ok) {
			if (autoCatchup) this.omitBoardEntrySummary = false;
			return;
		}
		const body = (await response.json()) as SharedPlanState;
		this.syncSharedPlanState(body);
		if (autoCatchup) {
			// Participants should see Flowy immediately, even before catch-up streams.
			if (
				body.sharedSession?.planAccepted &&
				body.participantState?.role !== 'host'
			) {
				this.isMinimized = false;
			}
			const joinOutcome = await this.maybeAutoJoinSharedStep(body);
			if (joinOutcome === 'deferred') {
				// Board graph not ready yet; retry shortly without racing catch-up.
				// Keep omitBoardEntrySummary true until that retry completes.
				return;
			}
			try {
				await this.maybeAutoRequestSharedCatchup(body, {
					emitStepIntro: joinOutcome === 'joined',
				});
			} finally {
				this.omitBoardEntrySummary = false;
			}
		}
	}

	private async maybeAutoJoinSharedStep(
		sharedPlanState: SharedPlanState
	): Promise<'joined' | 'already' | 'skipped' | 'deferred'> {
		const sharedSession = sharedPlanState.sharedSession;
		const participantState = sharedPlanState.participantState;
		if (
			!sharedSession?.sharedSessionId ||
			!sharedSession.planAccepted ||
			participantState?.role === 'host' ||
			!sharedSession.activeStepId
		) {
			return 'skipped';
		}
		if (participantState?.joinedStepId === sharedSession.activeStepId) {
			return 'already';
		}
		const boardReady = await this.waitForInitialBoardGraph();
		if (!boardReady) {
			window.setTimeout(() => void this.loadSharedPlanState({ autoCatchup: true }), 500);
			return 'deferred';
		}
		// Navigate onto the host's active step first; hold the building-block intro until
		// after catch-up so catch-up is the first Flowy message (no board-entry recap).
		const joined = await this.joinSharedPlanStepById(sharedSession.activeStepId, {
			announce: false,
			navigate: true,
			emitGuidance: false,
		});
		return joined ? 'joined' : 'skipped';
	}

	private shouldOmitBoardEntrySummary(): boolean {
		if (this.omitBoardEntrySummary) return true;
		const sharedSession = this.sharedPlanState?.sharedSession;
		const role = this.sharedPlanState?.participantState?.role;
		if (!sharedSession?.planAccepted || role === 'host' || !sharedSession.sharedSessionId) {
			return false;
		}
		// Participant still waiting on catch-up: board recap would duplicate it.
		return this.autoCatchupRequestedForSharedSessionId !== sharedSession.sharedSessionId;
	}

	private async waitForInitialBoardGraph(timeoutMs = 10_000): Promise<boolean> {
		const startedAt = Date.now();
		while (!globalThis.neuroCreate.graph && Date.now() - startedAt < timeoutMs) {
			// ShareDB and the Flowy history endpoint initialize independently for a newly joined user.
			// Waiting here prevents a second board load from destroying the first subscription mid-mount.
			// eslint-disable-next-line no-await-in-loop
			await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
		}
		return Boolean(globalThis.neuroCreate.graph);
	}

	private async maybeAutoRequestSharedCatchup(
		sharedPlanState: SharedPlanState,
		options?: { emitStepIntro?: boolean }
	) {
		const sharedSession = sharedPlanState.sharedSession;
		const participantState = sharedPlanState.participantState;
		if (
			!sharedSession?.sharedSessionId ||
			!sharedSession.planAccepted ||
			participantState?.role === 'host' ||
			this.autoCatchupRequestedForSharedSessionId === sharedSession.sharedSessionId
		) {
			return;
		}
		this.isMinimized = false;
		const delivered = await this.requestSharedCatchupMessage({ automatic: true });
		if (delivered) {
			this.autoCatchupRequestedForSharedSessionId = sharedSession.sharedSessionId;
			if (options?.emitStepIntro) {
				await this.emitSharedStepIntroAfterCatchup();
			}
			return;
		}
		// One soft retry so an empty Flowy panel is not left after a transient busy/network blip.
		if (this.autoCatchupRetryScheduledForSharedSessionId === sharedSession.sharedSessionId) {
			return;
		}
		this.autoCatchupRetryScheduledForSharedSessionId = sharedSession.sharedSessionId;
		window.setTimeout(() => {
			void this.maybeAutoRequestSharedCatchup(this.sharedPlanState || sharedPlanState, options);
		}, 1500);
	}

	private async emitSharedStepIntroAfterCatchup() {
		const activeStep = this.getSharedActiveStep();
		if (!activeStep || this.isBusy) return;
		this.onPlanBlockClick(activeStep, {
			emitGuidance: true,
			allowSharedParticipantNavigation: true,
			skipSharedJoin: true,
			navigationSource: 'shared_step_join',
		});
	}

	private async ensureSharedActivityPolling() {
		if (!this.boardId || this.sharedActivityPollTimer !== null) return;
		this.sharedActivityPollTimer = window.setInterval(() => {
			void this.pollSharedActivity();
		}, 5000);
		void this.pollSharedActivity();
	}

	private stopSharedActivityPolling() {
		if (this.sharedActivityPollTimer !== null) {
			window.clearInterval(this.sharedActivityPollTimer);
			this.sharedActivityPollTimer = null;
		}
	}

	private async pollSharedActivity() {
		if (!this.boardId || !this.sharedPlanState?.sharedSession) return;
		const currentUserId = document.body.dataset.userId || null;
		const query = this.lastSharedActivityId
			? `?afterActivityId=${encodeURIComponent(this.lastSharedActivityId)}&limit=20`
			: '?limit=1';
		const response = await fetch(`/board/${this.boardId}/agent/shared-activity${query}`).catch(
			() => null
		);
		if (!response?.ok) return;
		const body = (await response.json()) as { activities?: SharedFlowyActivity[] };
		const activities = body.activities || [];
		if (!activities.length) return;
		const chronological = [...activities].reverse();
		for (const activity of chronological) {
			if (currentUserId && String(activity.actorUserId || '') === currentUserId) {
				if (activity.type === 'shared_plan_advanced') {
					void this.loadSharedPlanState();
				}
				continue;
			}
			if (activity.type === 'participant_joined_session') {
				if (!this.isSharedPlanHost() || !activity.summaryText) continue;
				const joiner =
					activity.actorName?.trim() ||
					activity.summaryText.replace(/\s+joined the session\.?$/i, '').trim() ||
					'A collaborator';
				this.messages = [
					...this.messages,
					{
						role: 'assistant',
						content: hostParticipantJoinedSummary(joiner),
					},
				];
				this.isMinimized = false;
				continue;
			}
			if (SHARED_ACTIVITY_ASSISTANT_TYPES.has(activity.type) && activity.summaryText) {
				const targetStepId =
					activity.type === 'shared_plan_advanced' &&
					typeof activity.metadata?.toStepId === 'string'
						? activity.metadata.toStepId
						: '';
				const quickReplies: AgentQuickReply[] =
					activity.type === 'shared_plan_advanced'
						? [
								{
									id: `join_shared_step_${targetStepId || 'current'}`,
									label: 'Join host',
									value: `${FLOWY_JOIN_SHARED_STEP_PREFIX}${targetStepId}`,
								},
							]
						: [];
				this.messages = [
					...this.messages,
					{
						role: 'assistant',
						content: activity.summaryText,
						quickReplies: quickReplies.length ? quickReplies : undefined,
					},
				];
				this.focusNodeMentionedByFlowy(activity.summaryText);
				this.isMinimized = false;
			} else if (activity.summaryText && !SHARED_ACTIVITY_IGNORED_TYPES.has(activity.type)) {
				this.addNeutralEventMessage(activity.summaryText);
			}
			if (activity.type === 'shared_plan_advanced') {
				if (!this.isSharedPlanHost()) {
					this.isMinimized = false;
					this.isPlanCollapsed = false;
				}
				void this.loadSharedPlanState();
			}
		}
		this.lastSharedActivityId = activities[0]?._id || this.lastSharedActivityId;
		this.requestUpdate();
	}

	private reportSharedSessionLeave = () => {
		if (this.sharedLeaveReported || !this.boardId || !this.sessionId || !this.sharedPlanState?.sharedSession) {
			return;
		}
		this.sharedLeaveReported = true;
		const url = `/board/${this.boardId}/agent/shared-plan/leave`;
		const body = JSON.stringify({ sessionId: this.sessionId });
		if (navigator.sendBeacon) {
			const blob = new Blob([body], { type: 'application/json' });
			navigator.sendBeacon(url, blob);
			return;
		}
		void fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
			keepalive: true,
		}).catch(() => null);
	};

	private async loadHistory() {
		const response = await fetch(
			`/board/${this.boardId}/agent/history?sessionId=${encodeURIComponent(this.sessionId)}`
		);
		if (!response.ok) {
			return;
		}
		const data = (await response.json()) as {
			sessionId?: string;
			message?: string | null;
			messages: Array<{ role: string; content: string; quickReplies?: AgentQuickReply[] }>;
			plan: AgentPlan;
			planAccepted?: boolean;
			sessionState?: AgentSessionStateLite | null;
			engine?: string;
			researchLenses?: ResearchLensSummary[];
			buildingBlocks?: FlowyBuildingBlockOption[];
			live: {
				isRunning: boolean;
				workingLabel: string;
				streamingAssistantContent: string;
				toolSteps: ToolStep[];
			} | null;
		};
		if (data.sessionId && data.sessionId !== this.sessionId) {
			this.sessionId = data.sessionId;
			this.setAttribute('session-id', this.sessionId);
			if (this.boardId) {
				window.localStorage.setItem(getSessionStorageKey(this.boardId), this.sessionId);
			}
			this.subscribe();
			this.restoreDirectionPromptDismissal();
		}
		this.messages = (data.messages || [])
			.filter(
				(message) =>
					message.role === 'user' ||
					message.role === 'assistant' ||
					message.role === 'event' ||
					message.role === 'tool'
			)
			.map((message) => ({
				role: message.role as 'user' | 'assistant' | 'event' | 'tool',
				content: message.content,
				quickReplies: Array.isArray(message.quickReplies) ? message.quickReplies : undefined,
			}));
		if (!this.hasAcceptedPlanLock() || data.planAccepted === true) {
			this.plan = data.plan || null;
		}
		this.applyIncomingPlanAccepted(data.planAccepted);
		this.boardEngine = typeof data.engine === 'string' ? data.engine : '';
		this.researchLenses = Array.isArray(data.researchLenses) ? data.researchLenses : [];
		this.planBuildingBlocks = Array.isArray(data.buildingBlocks) ? data.buildingBlocks : [];
		if (data.sessionState) {
			this.applySessionStateToasts(data.sessionState);
		}
		const graph = globalThis.neuroCreate.graph as { nodes?: unknown[] } | undefined;
		const boardHasNodes = Array.isArray(graph?.nodes) && graph!.nodes.length > 0;
		this.boardInitialized =
			this.messages.length > 0 || Boolean(this.plan?.steps?.length) || boardHasNodes;
		if (this.messages.length > 0) {
			this.suppressStartupEventsUntilWelcome = false;
		}
		const currentBoard = (globalThis.neuroCreate.graph?.graphMode || 'inspire') as FlowyPlanBoard;
		this.syncActivePlanStepForBoard(currentBoard);
		await this.loadSharedPlanState({ autoCatchup: true });
		this.syncPlanSidebarState();
		this.revealRestoredPlanSidebar();
		if (data.live) {
			this.isBusy = data.live.isRunning;
			this.workingLabel = data.live.workingLabel;
			this.streamingAssistantContent = data.live.streamingAssistantContent;
			this.toolSteps = data.live.toolSteps || [];
			this.appendInFlightToolMessagesFromLiveState(this.toolSteps);
		}
		// Existing session, empty memory, but board is already initialized: send welcome.
		if (
			this.boardInitialized &&
			this.messages.length === 0 &&
			!this.hasPlanSteps() &&
			!data.live?.isRunning &&
			!this.hasOpenAlert()
		) {
			void this.requestSessionWelcome();
		}
		this.lastSessionState = data.sessionState || this.lastSessionState;
		this.requestUpdate();
		this.scrollMessagesToBottom();
		this.resetIdleAssistTimer();
	}

	private async requestSessionWelcome(onboarding?: OnboardingWelcomeContext) {
		if (!this.boardId || !this.sessionId) {
			return;
		}
		if (this.hasPlanSteps()) {
			return;
		}
		if (this.welcomeRequestedForSession === this.sessionId) {
			return;
		}
		this.welcomeRequestedForSession = this.sessionId;
		this.emitFlowyWelcomeShownOnReply = Boolean(onboarding);

		this.isMinimized = false;
		this.isBusy = true;
		// this.workingLabel = 'Opening Flowy...';
		this.streamingAssistantContent = '';
		this.toolSteps = [];
		this.receivedLiveEvent = false;
		this.expectedAssistantMessages =
			this.messages.filter((entry) => entry.role === 'assistant').length + 1;
		this.requestUpdate();
		this.scrollMessagesToBottom();

		const response = await fetch(`/board/${this.boardId}/agent/welcome`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ sessionId: this.sessionId, onboarding }),
		}).catch(() => null);

		if (!response || !response.ok) {
			this.welcomeRequestedForSession = null;
			this.isBusy = false;
			this.workingLabel = 'Flowy could not open the session.';
			this.requestUpdate();
			return;
		}

		const body = (await response.json()) as ({ skipped?: boolean } & Partial<AgentHttpReply>);
		if (body.skipped) {
			this.suppressStartupEventsUntilWelcome = false;
			this.isBusy = false;
			this.workingLabel = '';
			this.requestUpdate();
			return;
		}

		await this.simulateAssistantReply({
			message: body.message || '',
			goal: body.goal ?? null,
			plan: body.plan ?? null,
			planAccepted: body.planAccepted,
			nodeRefs: body.nodeRefs || [],
			quickReplies: body.quickReplies || [],
			effects: body.effects || [],
		});
	}

	private hasOpenAlert(): boolean {
		return Boolean(document.querySelector('.alert-show dialog.alert-window'));
	}

	private async simulateAssistantReply(reply: AgentHttpReply) {
		const content = reply.message || '';
		this.syncSessionGoal(reply.goal);
		if (content) {
			this.suppressStartupEventsUntilWelcome = false;
		}
		this.applyBackendFlowyEffects(reply.effects || []);
		const previousPlanAccepted = this.planAccepted;
		// this.workingLabel = 'Responding...';
		this.streamingAssistantContent = '';
		this.requestUpdate();
		this.scrollMessagesToBottom();

		const chunks = content.match(/.{1,24}(\s|$)|\S+/g) || [content];
		for (const chunk of chunks) {
			this.streamingAssistantContent = `${this.streamingAssistantContent}${chunk}`;
			this.requestUpdate();
			this.scrollMessagesToBottom();
			// Simulated typing effect on the client.
			// eslint-disable-next-line no-await-in-loop
			await new Promise<void>((resolve) => window.setTimeout(resolve, 35));
		}

		if (!this.hasAcceptedPlanLock() || reply.planAccepted === true) {
			this.plan = reply.plan || this.plan;
		}
		this.applyIncomingPlanAccepted(reply.planAccepted);
		this.syncPlanSidebarState({ expandWhenPending: true });
		if (!previousPlanAccepted && this.planAccepted && this.plan?.steps?.length) {
			void this.loadSharedPlanState();
			this.onPlanBlockClick(this.plan.steps[0], {
				emitGuidance: true,
				navigationSource: 'plan_acceptance',
			});
		}
		this.messages = [
			...this.messages,
			{ role: 'assistant', content, quickReplies: reply.quickReplies || undefined },
		];
		this.focusNodeMentionedByFlowy(content, reply.nodeRefs || []);
		this.logDebug('ASSISTANT_MESSAGE', {
			source: 'simulateAssistantReply',
			message: content,
			quickReplies: reply.quickReplies || [],
		});
		this.streamingAssistantContent = '';
		this.toolSteps = [];
		this.workingLabel = '';
		this.isBusy = false;
		this.startDeferredBootstrapTutorial();
		if (this.emitFlowyWelcomeShownOnReply) {
			this.emitFlowyWelcomeShownOnReply = false;
			flowyWelcomeShown();
		}
		this.stopHistoryPolling();
		this.requestUpdate();
		this.scrollMessagesToBottom();
		this.resetIdleAssistTimer();
	}

	private stopHistoryPolling() {
		if (this.historyPollTimer !== null) {
			window.clearInterval(this.historyPollTimer);
			this.historyPollTimer = null;
		}
		if (this.historyPollStartTimer !== null) {
			window.clearTimeout(this.historyPollStartTimer);
			this.historyPollStartTimer = null;
		}
	}

	private startHistoryPolling() {
		this.stopHistoryPolling();
		this.historyPollStartTimer = window.setTimeout(() => {
			if (this.receivedLiveEvent) {
				return;
			}
			let attempts = 0;
			this.historyPollTimer = window.setInterval(async () => {
			attempts += 1;
			const response = await fetch(
				`/board/${this.boardId}/agent/history?sessionId=${encodeURIComponent(this.sessionId)}`
			).catch(() => null);
			if (!response || !response.ok) {
				if (attempts >= 60) {
					this.stopHistoryPolling();
					this.isBusy = false;
					this.workingLabel = 'Flowy did not respond in time.';
					this.requestUpdate();
				}
				return;
			}

			const data = (await response.json()) as {
				messages: Array<{ role: string; content: string }>;
				plan: AgentPlan;
				planAccepted?: boolean;
				live: {
					isRunning: boolean;
					workingLabel: string;
					streamingAssistantContent: string;
					toolSteps: ToolStep[];
				} | null;
			};

			const nextMessages = (data.messages || [])
				.filter(
					(message) =>
						message.role === 'user' ||
						message.role === 'assistant' ||
						message.role === 'event' ||
						message.role === 'tool'
				)
				.map((message) => ({
					role: message.role as 'user' | 'assistant' | 'event' | 'tool',
					content: message.content,
				}));

			const assistantCount = nextMessages.filter((message) => message.role === 'assistant').length;
			if (data.live) {
				this.isBusy = data.live.isRunning;
				this.workingLabel = data.live.workingLabel;
				this.streamingAssistantContent = data.live.streamingAssistantContent;
				this.toolSteps = data.live.toolSteps || [];
			}
			if (this.receivedLiveEvent && this.streamingAssistantContent) {
				if (data.plan?.steps?.length && (!this.hasAcceptedPlanLock() || data.planAccepted === true)) {
					this.plan = data.plan;
					this.applyIncomingPlanAccepted(data.planAccepted);
					this.syncPlanSidebarState({ expandWhenPending: true });
				}
				this.requestUpdate();
				this.scrollMessagesToBottom();
				return;
			}

			if (this.streamingAssistantContent && assistantCount < this.expectedAssistantMessages) {
				this.messages = [
					...nextMessages,
					{ role: 'assistant', content: this.streamingAssistantContent },
				];
			} else {
				this.messages = nextMessages;
			}
			this.appendInFlightToolMessagesFromLiveState(this.toolSteps);
			if (!this.hasAcceptedPlanLock() || data.planAccepted === true) {
				this.plan = data.plan || this.plan;
			}
			this.applyIncomingPlanAccepted(data.planAccepted);
			this.requestUpdate();
			this.scrollMessagesToBottom();

			if (assistantCount >= this.expectedAssistantMessages) {
				const latestAssistantMessage = [...nextMessages]
					.reverse()
					.find((message) => message.role === 'assistant');
				if (latestAssistantMessage) {
					this.focusNodeMentionedByFlowy(latestAssistantMessage.content);
				}
				this.isBusy = false;
				this.workingLabel = '';
				this.stopHistoryPolling();
				this.requestUpdate();
				return;
			}

			if (attempts >= 60) {
				this.stopHistoryPolling();
				this.isBusy = false;
				this.workingLabel = 'Flowy did not respond in time.';
				this.requestUpdate();
			}
			}, 1000);
		}, 2000);
	}

	private handleAgentEvent(event: AgentEvent) {
		this.logDebug('AGENT_EVENT', { eventType: event.type, event });
		this.resetIdleAssistTimer();
		this.receivedLiveEvent = true;
		if (event.type === 'start') {
			this.isBusy = true;
			this.workingLabel = 'Planning next step...';
		} else if (event.type === 'tool_call') {
			this.isBusy = true;
			this.workingLabel = `Using tool: ${event.label}`;
			this.applyBackendFlowyEffects(event.effects || []);
			this.announcedLiveToolCallIds.add(event.callId);
			this.toolCallNamesById.set(event.callId, event.name);
			this.addToolActivityMessage(`Running ${event.label}...`);
			void this.postAgentEvent('tool_started', { tool: event.name, callId: event.callId });
			this.toolSteps = [
				...this.toolSteps.filter((step) => step.callId !== event.callId),
				{
					callId: event.callId,
					name: event.name,
					label: event.label,
					rationale: event.rationale,
					arguments: event.arguments,
					status: 'running',
				},
			];
		} else if (event.type === 'tool_result') {
			this.workingLabel = 'Processing tool results...';
			this.applyBackendFlowyEffects(event.effects || []);
			generateUi.hideGenerateProgress('agent');
			const finishedStep = this.toolSteps.find((step) => step.callId === event.callId);
			void this.postAgentEvent('tool_finished', {
				tool: finishedStep?.name || finishedStep?.label || event.callId,
				callId: event.callId,
				output: event.output,
			});
			this.toolSteps = this.toolSteps.map((step) =>
				step.callId === event.callId ? { ...step, status: 'completed' } : step
			);
			const activityMessage = this.formatToolActivityMessage(finishedStep, event);
			if (activityMessage) {
				this.addToolActivityMessage(activityMessage);
			}
			const toolName = finishedStep?.name || this.toolCallNamesById.get(event.callId) || '';
			if (
				!this.hasAcceptedPlanLock() &&
				(toolName === 'proposePlan' ||
					toolName.endsWith('.proposePlan') ||
					toolName.endsWith('/proposePlan'))
			) {
				this.applyPlanFromToolOutput(event.output);
			}
			if (event.nodeRefs?.length) {
				this.focusNodesIfNeeded(event.nodeRefs, true);
			}
		} else if (event.type === 'assistant_delta') {
			this.isBusy = true;
			// this.workingLabel = 'Responding...';
			this.streamingAssistantContent = this.streamingAssistantContent + event.delta;
			this.scrollMessagesToBottom();
		} else if (event.type === 'plan') {
			if (!this.hasAcceptedPlanLock()) {
				this.plan = event.plan;
				this.syncPlanSidebarState({ expandWhenPending: true });
			}
		} else if (event.type === 'plan_accepted') {
			this.planAccepted = true;
			this.syncPlanSidebarState();
			void this.loadSharedPlanState();
			this.addNeutralEventMessage('Plan accepted');
		} else if (event.type === 'final_message') {
			this.applyBackendFlowyEffects(event.effects || []);
			generateUi.hideGenerateProgress('agent');
			this.syncSessionGoal(event.goal);
			// Preserve an already received plan event if final_message carries null.
			const previousPlanAccepted = this.planAccepted;
			if (!this.hasAcceptedPlanLock() || event.planAccepted === true) {
				this.plan = event.plan || this.plan;
			}
			this.applyIncomingPlanAccepted(event.planAccepted);
			this.syncPlanSidebarState({ expandWhenPending: !this.planAccepted });
			if (!previousPlanAccepted && this.planAccepted && this.plan?.steps?.length) {
				void this.loadSharedPlanState();
			}
			this.messages = [
				...this.messages,
				{
					role: 'assistant',
					content: event.message,
					quickReplies: event.quickReplies?.length ? event.quickReplies : undefined,
				},
			];
			if (event.materializedNodeIds?.length) {
				this.focusMaterializedNodes(event.materializedNodeIds);
			} else {
				this.focusNodeMentionedByFlowy(event.message, event.nodeRefs || []);
			}
			this.logDebug('ASSISTANT_MESSAGE', {
				source: 'agent_final_message',
				message: event.message,
				quickReplies: event.quickReplies || [],
			});
			this.streamingAssistantContent = '';
			this.toolSteps = [];
			this.workingLabel = '';
			this.isBusy = false;
			this.stopHistoryPolling();
			this.scrollMessagesToBottom();
			this.resetIdleAssistTimer();
		} else if (event.type === 'done') {
			generateUi.hideGenerateProgress('agent');
			this.isBusy = false;
			if (!this.streamingAssistantContent) {
				this.workingLabel = '';
			}
			this.stopHistoryPolling();
		} else if (event.type === 'error') {
			generateUi.hideGenerateProgress('agent');
			this.isBusy = false;
			this.workingLabel = event.message;
			this.streamingAssistantContent = '';
			this.toolSteps = this.toolSteps.map((step) =>
				step.status === 'running' ? { ...step, status: 'failed' } : step
			);
			this.stopHistoryPolling();
		} else if (event.type === 'tool_timeline') {
			generateUi.updateBackendGenerateProgress(event.message);
			this.addToolActivityMessage(event.message, { persist: false });
		}
		this.requestUpdate();
	}

	private textareaResize = () => {
		const ta = this.querySelector<HTMLTextAreaElement>('.agent-chat-panel__input');
		if (!ta) {
			return;
		}
		ta.style.height = 'auto';
		const style = getComputedStyle(ta);
		const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.45;
		const pad = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
		const minH = lineHeight * 2 + pad;
		const maxH = lineHeight * 8 + pad;
		const next = Math.min(Math.max(ta.scrollHeight, minH), maxH);
		ta.style.height = `${next}px`;
	};

	private onComposerInput = () => {
		this.textareaResize();
		const input = this.querySelector<HTMLTextAreaElement>('.agent-chat-panel__input');
		const value = input?.value || '';
		const shouldOpen = value.trim() === '/' || /^\/\w*/.test(value);
		if (this.isSlashCommandMenuOpen !== shouldOpen) {
			this.isSlashCommandMenuOpen = shouldOpen;
			this.requestUpdate();
		}
	};

	private getCurrentFlowyBoard(): FlowyPlanBoard {
		const graphMode = globalThis.neuroCreate.graph?.graphMode;
		if (graphMode === 'inspire' || graphMode === 'ideate' || graphMode === 'moodboard') {
			return graphMode;
		}
		const activeStep = this.plan?.steps?.find((step) => step.id === this.activePlanStepId);
		if (activeStep?.board) {
			return activeStep.board;
		}
		return this.lastSessionState?.activeBoard || 'inspire';
	}

	private getSlashCommandsForCurrentBoard(): FlowySlashCommand[] {
		const board = this.getCurrentFlowyBoard();
		if (board === 'ideate') {
			return IDEATE_SLASH_COMMANDS;
		}
		if (board === 'moodboard') {
			return SYNTHESISE_SLASH_COMMANDS;
		}
		return INSPIRE_SLASH_COMMANDS;
	}

	private focusSlashCommandButton(offset: number) {
		const buttons = Array.from(
			this.querySelectorAll<HTMLButtonElement>('.agent-chat-panel__slash-command')
		);
		if (!buttons.length) return;
		const activeIndex = buttons.findIndex((button) => button === document.activeElement);
		const nextIndex =
			activeIndex >= 0
				? (activeIndex + offset + buttons.length) % buttons.length
				: offset > 0
					? 0
					: buttons.length - 1;
		buttons[nextIndex]?.focus();
	}

	private onComposerKeydown = (event: KeyboardEvent) => {
		event.stopPropagation();
		if (this.isSlashCommandMenuOpen) {
			if (event.key === 'Escape') {
				event.preventDefault();
				this.isSlashCommandMenuOpen = false;
				this.requestUpdate();
				return;
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				requestAnimationFrame(() => this.focusSlashCommandButton(1));
				return;
			}
			if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
				event.preventDefault();
				requestAnimationFrame(() => this.focusSlashCommandButton(1));
				return;
			}
		}
		if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
			return;
		}
		event.preventDefault();
		void this.sendMessage(event);
	};

	private onSlashCommandButtonKeydown = (event: KeyboardEvent) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			this.focusSlashCommandButton(1);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			this.focusSlashCommandButton(-1);
		} else if (event.key === 'Escape') {
			event.preventDefault();
			this.isSlashCommandMenuOpen = false;
			this.querySelector<HTMLTextAreaElement>('.agent-chat-panel__input')?.focus();
			this.requestUpdate();
		}
	};

	private async runSlashCommand(command: string) {
		const input = this.querySelector<HTMLTextAreaElement>('.agent-chat-panel__input');
		if (input) {
			input.value = '';
			requestAnimationFrame(() => this.textareaResize());
		}
		this.isSlashCommandMenuOpen = false;
		await this.submitUserMessage(command, command);
	}

	private pinPanelRect(
		panel: HTMLElement,
		rect: { left: number; top: number; width: number; height: number },
	) {
		panel.style.left = `${rect.left}px`;
		panel.style.top = `${rect.top}px`;
		panel.style.width = `${rect.width}px`;
		panel.style.height = `${rect.height}px`;
		panel.style.right = 'auto';
		panel.style.bottom = 'auto';
		panel.style.transform = '';
	}

	private clearPanelInlineGeometry(panel: HTMLElement) {
		panel.style.left = '';
		panel.style.top = '';
		panel.style.width = '';
		panel.style.height = '';
		panel.style.right = '';
		panel.style.bottom = '';
		panel.style.transform = '';
	}

	private endPanelTransition(panel: HTMLElement) {
		window.setTimeout(() => {
			panel.classList.remove('agent-chat-panel--animating');
		}, this.panelTransitionMs);
	}

	private toggleMinimized = () => {
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}

		if (!this.isMinimized) {
			const rect = panel.getBoundingClientRect();
			this.expandedPanelRect = {
				left: rect.left,
				top: rect.top,
				width: rect.width,
				height: rect.height,
			};
			this.pinPanelRect(panel, rect);
			panel.classList.add('agent-chat-panel--animating');
			this.isMinimized = true;
			this.requestUpdate();
			requestAnimationFrame(() => {
				this.clearPanelInlineGeometry(panel);
				this.endPanelTransition(panel);
			});
			return;
		}

		const minimizedRect = panel.getBoundingClientRect();
		this.pinPanelRect(panel, minimizedRect);
		panel.classList.add('agent-chat-panel--animating');
		this.isMinimized = false;
		this.requestUpdate();
		requestAnimationFrame(() => {
			const saved = this.expandedPanelRect;
			if (!saved) {
				this.clearPanelInlineGeometry(panel);
				panel.classList.remove('agent-chat-panel--animating');
				return;
			}
			void panel.offsetHeight;
			const width = Math.min(saved.width, window.innerWidth - 16);
			const height = Math.min(saved.height, window.innerHeight - 16);
			const left = Math.min(Math.max(saved.left, 0), Math.max(0, window.innerWidth - width));
			const top = Math.min(Math.max(saved.top, 0), Math.max(0, window.innerHeight - height));
			this.pinPanelRect(panel, { left, top, width, height });
			this.endPanelTransition(panel);
		});
	};

	private releasePanelDimensions() {
		if (this.isDragging || this.isMinimized) {
			return;
		}
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}
		panel.style.width = '';
		panel.style.height = '';
	}

	private syncExpandedPanelRectWidth() {
		if (!this.expandedPanelRect) {
			return;
		}
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}
		// Drop saved width so restore uses CSS plan-addon sizing after collapse/expand.
		this.expandedPanelRect = {
			...this.expandedPanelRect,
			width: panel.getBoundingClientRect().width,
			height: panel.getBoundingClientRect().height,
		};
	}

	private togglePlanCollapsed = () => {
		this.isPlanCollapsed = !this.isPlanCollapsed;
		this.releasePanelDimensions();
		this.syncExpandedPanelRectWidth();
		this.requestUpdate();
	};

	private onDragStart = (event: PointerEvent) => {
		if (this.isMinimized) {
			return;
		}
		const target = event.target as HTMLElement | null;
		if (target?.closest('button')) {
			return;
		}
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}
		const rect = panel.getBoundingClientRect();
		this.isDragging = true;
		this.dragPointerId = event.pointerId;
		this.dragOffsetX = event.clientX - rect.left;
		this.dragOffsetY = event.clientY - rect.top;
		panel.style.width = `${rect.width}px`;
		panel.style.height = `${rect.height}px`;
		panel.style.transition = 'none';
		window.addEventListener('pointermove', this.onDragMove);
		window.addEventListener('pointerup', this.onDragEnd);
		window.addEventListener('pointercancel', this.onDragEnd);
	};

	private onDragMove = (event: PointerEvent) => {
		if (!this.isDragging || this.dragPointerId !== event.pointerId) {
			return;
		}
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (!panel) {
			return;
		}
		const width = panel.offsetWidth;
		const height = panel.offsetHeight;
		const x = event.clientX - this.dragOffsetX;
		const y = event.clientY - this.dragOffsetY;
		const boundedX = Math.max(0, Math.min(x, window.innerWidth - width));
		const boundedY = Math.max(0, Math.min(y, window.innerHeight - height));

		panel.style.left = `${boundedX}px`;
		panel.style.top = `${boundedY}px`;
		panel.style.right = 'auto';
		panel.style.bottom = 'auto';
		panel.style.transform = 'none';
	};

	private onDragEnd = (event: PointerEvent) => {
		if (!this.isDragging || this.dragPointerId !== event.pointerId) {
			return;
		}
		this.isDragging = false;
		this.dragPointerId = null;
		this.userHasDraggedPanel = true;
		const panel = this.querySelector<HTMLElement>('.agent-chat-panel');
		if (panel) {
			panel.style.transition = '';
			// Keep dragged position but let CSS width/height track plan sidebar state.
			panel.style.width = '';
			panel.style.height = '';
		}
		window.removeEventListener('pointermove', this.onDragMove);
		window.removeEventListener('pointerup', this.onDragEnd);
		window.removeEventListener('pointercancel', this.onDragEnd);
	};

	protected firstUpdated(changedProperties: PropertyValues): void {
		super.firstUpdated(changedProperties);
		requestAnimationFrame(() => {
			this.textareaResize();
			this.syncPanelAnchorPosition();
		});
		this.addEventListener('click', (event) => {
			const target = event.target as HTMLElement | null;
			const nodeLink = target?.closest<HTMLElement>('.agent-chat-panel__node-link');
			const nodeUid = nodeLink?.dataset.nodeUid;
			if (nodeUid) {
				event.preventDefault();
				this.focusNodeById(nodeUid);
				void this.postAgentEvent('node_selected', { nodeUid });
			}
		});
	}

	private scrollMessagesToBottom() {
		requestAnimationFrame(() => {
			const messageList = this.querySelector<HTMLElement>('.agent-chat-panel__messages');
			if (messageList) {
				messageList.scrollTop = messageList.scrollHeight;
			}
		});
	}

	protected updated(changedProperties: PropertyValues): void {
		super.updated(changedProperties);
		if (changedProperties.has('isPlanCollapsed')) {
			this.releasePanelDimensions();
			this.syncExpandedPanelRectWidth();
		}
		if (changedProperties.has('isBusy') || changedProperties.has('isMinimized')) {
			requestAnimationFrame(() => this.textareaResize());
		}
	}

	private async sendMessage(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		const input = this.querySelector<HTMLTextAreaElement>('.agent-chat-panel__input');
		const message = input?.value.trim() || '';
		if (input) {
			input.value = '';
			requestAnimationFrame(() => this.textareaResize());
		}
		this.isSlashCommandMenuOpen = false;
		await this.submitUserMessage(message);
	}

	private async submitUserMessage(
		message: string,
		displayMessage = message,
		options?: { skipUserBubble?: boolean }
	) {
		const normalized = message.trim();
		const display = displayMessage.trim() || normalized;
		if (!normalized || this.isBusy || this.isPlanSaving) {
			return;
		}

		if (!options?.skipUserBubble) {
			this.messages = [...this.messages, { role: 'user', content: display }];
		}
		this.pendingRetryMessage = { message: normalized, display };
		this.resetIdleAssistTimer();
		this.logDebug('USER_MESSAGE', { message: normalized, displayMessage: display });
		this.isBusy = true;
		this.workingLabel = 'Thinking...';
		this.streamingAssistantContent = '';
		this.toolSteps = [];
		this.receivedLiveEvent = false;
		this.expectedAssistantMessages =
			this.messages.filter((entry) => entry.role === 'assistant').length + 1;
		this.requestUpdate();
		this.scrollMessagesToBottom();

		const response = await fetch(`/board/${this.boardId}/agent/chat`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				sessionId: this.sessionId,
				message: normalized,
				activeNodeId:
					globalThis.neuroCreate.graph?.activeNode?.uid ||
					globalThis.neuroCreate.graph?.currentUser?.activeNode ||
					undefined,
				buildingBlockIds:
					!this.planAccepted && this.canEditPlan() && this.plan?.steps?.length
						? this.plan.steps
								.map((step) => step.buildingBlockId)
								.filter((id): id is string => Boolean(id))
						: undefined,
			}),
		}).catch(() => null);

		if (!response || !response.ok) {
			this.logDebug('SYSTEM_EVENT', {
				message: 'flowy_chat_http_error',
				httpStatus: response?.status ?? null,
				ok: response?.ok ?? false,
			});
			this.showFlowyUnavailableRetry();
			return;
		}

		this.pendingRetryMessage = null;

		const body = (await response.json()) as AgentHttpReply;
		const responseToolMessages = Array.isArray(body.toolMessages) ? body.toolMessages : [];
		for (const rawMessage of responseToolMessages) {
			const toolMessage = typeof rawMessage === 'string' ? rawMessage.trim() : '';
			if (toolMessage) {
				this.addToolActivityMessage(toolMessage, { persist: false });
			}
		}
		// Prefer true live streaming from backend events when available.
		// Keep simulated streaming only as a fallback (or for templated endpoints).
		if (this.receivedLiveEvent) {
			this.syncSessionGoal(body.goal);
			if (!this.hasAcceptedPlanLock() || body.planAccepted === true) {
				this.plan = body.plan || this.plan;
			}
			this.applyIncomingPlanAccepted(body.planAccepted);
			this.syncPlanSidebarState({ expandWhenPending: true });
			if (!this.isBusy) {
				this.streamingAssistantContent = '';
				this.workingLabel = '';
			}
			this.requestUpdate();
			this.scrollMessagesToBottom();
			return;
		}

		await this.simulateAssistantReply({
			message: body.message || '',
			goal: body.goal ?? null,
			plan: body.plan ?? null,
			planAccepted: body.planAccepted,
			nodeRefs: body.nodeRefs || [],
			quickReplies: body.quickReplies || [],
			effects: body.effects || [],
		});
	}

	private onQuickReplyClick(reply: AgentQuickReply) {
		if (reply.id === FLOWY_RETRY_QUICK_REPLY.id) {
			if (!this.pendingRetryMessage || this.isBusy) {
				return;
			}
			this.popRetryErrorMessage();
			void this.submitUserMessage(this.pendingRetryMessage.message, this.pendingRetryMessage.display, {
				skipUserBubble: true,
			});
			return;
		}
		if (reply.id === 'accept_plan') {
			void this.acceptPlan();
			return;
		}
		if (this.handleMediaFeedbackQuickReply(reply)) {
			return;
		}
		if (reply.value.startsWith(FLOWY_CONTINUE_TO_BLOCK_PREFIX)) {
			const buildingBlockId = reply.value.slice(FLOWY_CONTINUE_TO_BLOCK_PREFIX.length);
			const matchingStep = this.plan?.steps?.find(
				(step) => step.buildingBlockId === buildingBlockId
			);
			if (matchingStep) {
				this.onPlanBlockClick(matchingStep);
			}
			return;
		}
		if (reply.id === 'share_to_team' || reply.value.startsWith(FLOWY_SHARE_TO_TEAM_VALUE)) {
			const nodeUid = reply.value.startsWith(`${FLOWY_SHARE_TO_TEAM_VALUE}:`)
				? reply.value.slice(`${FLOWY_SHARE_TO_TEAM_VALUE}:`.length)
				: null;
			void this.shareCurrentWorkToTeam(nodeUid);
			return;
		}
		if (reply.value.startsWith(FLOWY_STAY_ON_BLOCK_PREFIX)) {
			void this.submitUserMessage('Stay on this step', reply.label);
			return;
		}
		if (
			reply.value === FLOWY_VIEW_POSITIONING_FOUNDATIONS_VALUE ||
			reply.value === FLOWY_CONFIGURE_POSITIONING_FOUNDATIONS_VALUE
		) {
			window.dispatchEvent(
				new CustomEvent('flowy-configure-positioning-foundations', { detail: reply.target })
			);
			return;
		}
		if (reply.value.startsWith(FLOWY_JOIN_SHARED_STEP_PREFIX)) {
			const targetStepId = reply.value.slice(FLOWY_JOIN_SHARED_STEP_PREFIX.length) || null;
			void this.joinSharedPlanStepById(targetStepId, {
				announce: true,
				navigate: true,
				emitGuidance: true,
			});
			return;
		}
		this.activateBoardNodeFromQuickReply(reply);
		if (reply.id !== 'other' && reply.label.trim()) {
			this.pendingSessionGoalLabel = reply.label.trim();
			this.sessionGoal = reply.label.trim();
		}
		void this.submitUserMessage(reply.value, reply.label);
	}

	/**
	 * "I like it" / "Add it to the board" after image/video generation must act on
	 * the Generate panel result locally. Sending chat text alone cannot heart or
	 * place the already-rendered media.
	 */
	private handleMediaFeedbackQuickReply(reply: AgentQuickReply): boolean {
		const like = isMediaLikeQuickReply(reply);
		const addToBoard = isMediaAddToBoardQuickReply(reply);
		if (!like && !addToBoard) return false;

		const label = reply.label.trim() || (like ? 'I like it' : 'Add it to the board');
		this.messages = [...this.messages, { role: 'user', content: label }];
		this.requestUpdate();
		this.scrollMessagesToBottom();

		const ok = like
			? generateUi.likeFlowyGenerateResult()
			: generateUi.addFlowyGenerateResultToBoard();
		if (!ok) {
			const kind = /video/i.test(reply.value) || /video/i.test(reply.label) ? 'video' : 'image';
			const failure = like
				? `I couldn't find a generated ${kind} to heart. Open the Generate panel and try again.`
				: `I couldn't find a generated ${kind} to add. Open the Generate panel and try again.`;
			this.messages = [...this.messages, { role: 'assistant', content: failure }];
			this.requestUpdate();
			this.scrollMessagesToBottom();
		}
		return true;
	}

	/** When a research quick reply targets a board node, select it and rebind Search. */
	private activateBoardNodeFromQuickReply(reply: AgentQuickReply): void {
		const target = reply.target;
		const looksLikeSearchFollowUp =
			reply.id.startsWith('research_next_') ||
			/^run search\b/i.test(reply.value.trim()) ||
			target?.type === 'board-node';
		if (!looksLikeSearchFollowUp) return;

		const graph = globalThis.neuroCreate.graph;
		const nodes = graph?.nodes || [];
		const uid = target?.type === 'board-node' ? target.uid || target.id : undefined;
		const label =
			(target?.type === 'board-node' ? target.label || target.title : undefined) ||
			reply.value.match(/\baround\s+(.+)$/i)?.[1]?.trim();
		const node =
			(uid ? nodes.find((candidate) => candidate.uid === uid) : undefined) ||
			(label
				? nodes.find(
						(candidate) =>
							(candidate.label || '').trim().toLowerCase() === label.trim().toLowerCase()
					)
				: undefined);
		if (!node) return;

		this.focusNodeById(node.uid);
		searchUi.rememberSearchSourceNode(node);
		// Rebind Search breadcrumbs immediately; results arrive when the agent search completes.
		searchUi.openBackendSearchPanel({
			selectedNodeIds: [node.uid],
			selectedNodeLabels: node.label ? [node.label] : undefined,
		});
	}

	private async shareCurrentWorkToTeam(nodeUid?: string | null) {
		const graph = globalThis.neuroCreate.graph;
		if (!graph) return;
		if (graph.graphMode === 'moodboard') {
			const node =
				(nodeUid ? graph.nodes.find((candidate) => candidate.uid === nodeUid) : undefined) ||
				graph.activeNode;
			const share = (graph as { share?: (sharedNode: unknown) => void }).share;
			if (node && typeof share === 'function') {
				share(node);
				return;
			}
			globalThis.neuroCreate.switchToMode?.('moodboard', true, false);
			return;
		}
		if (graph.graphMode === 'ideate') {
			try {
				const likes = await getUserLikes(this.boardId, undefined, false);
				const latest = [...likes].reverse().find((like) => like.sharedToTeam === false && like._id);
				if (latest?._id) {
					await shareLikeToTeam(this.boardId, latest._id);
				}
			} catch (error) {
				console.error('Unable to share the latest personal output with the team', error);
			}
			globalThis.neuroCreate.switchToMode?.('ideate', true, false);
		}
	}

	private showFlowyUnavailableRetry() {
		this.isBusy = false;
		this.workingLabel = '';
		this.streamingAssistantContent = '';
		this.toolSteps = [];
		this.stopHistoryPolling();
		this.popRetryErrorMessage();
		this.messages = [
			...this.messages,
			{
				role: 'assistant',
				content: FLOWY_RETRY_UNAVAILABLE_MESSAGE,
				quickReplies: [FLOWY_RETRY_QUICK_REPLY],
			},
		];
		this.expectedAssistantMessages = this.messages.filter((entry) => entry.role === 'assistant').length;
		this.logDebug('ASSISTANT_MESSAGE', {
			source: 'flowy_unavailable_retry',
			message: FLOWY_RETRY_UNAVAILABLE_MESSAGE,
			quickReplies: [FLOWY_RETRY_QUICK_REPLY],
		});
		this.requestUpdate();
		this.scrollMessagesToBottom();
	}

	private popRetryErrorMessage(): boolean {
		const last = this.messages[this.messages.length - 1];
		if (last?.role === 'assistant' && last.content === FLOWY_RETRY_UNAVAILABLE_MESSAGE) {
			this.messages = this.messages.slice(0, -1);
			return true;
		}
		return false;
	}

	private async resetSession() {
		window.localStorage.removeItem(this.getDirectionPromptStorageKey());
		this.directionPromptDismissed = false;
		this.directionPromptExpanded = false;
		this.isPlanAddMenuOpen = false;
		this.isPlanSaving = false;
		await fetch(`/board/${this.boardId}/agent/reset`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ sessionId: this.sessionId }),
		});
		this.messages = [];
		this.plan = null;
		this.planAccepted = false;
		this.pendingRetryMessage = null;
		this.workingLabel = '';
		this.streamingAssistantContent = '';
		this.toolSteps = [];
		this.isBusy = false;
		this.receivedLiveEvent = false;
		this.stopHistoryPolling();
		this.welcomeRequestedForSession = null;
		this.boardInitialized = false;
		this.omitBoardEntrySummary = false;
		if (this.boardInitializationTimer !== null) {
			window.clearTimeout(this.boardInitializationTimer);
			this.boardInitializationTimer = null;
		}
		this.activePlanStepId = null;
		this.isMinimized = false;
		this.isPlanCollapsed = false;
		if (this.idleAssistTimer !== null) {
			window.clearTimeout(this.idleAssistTimer);
			this.idleAssistTimer = null;
		}
		this.idleCheckInFlight = false;
		this.requestUpdate();
		void this.loadHistory();
	}

	private onPlanBlockClick(
		step: AgentPlanStep,
		options?: {
			emitGuidance?: boolean;
			allowSharedParticipantNavigation?: boolean;
			skipSharedJoin?: boolean;
			navigationSource?:
				| 'plan_step_select'
				| 'shared_step_join'
				| 'host_step_advance'
				| 'plan_acceptance';
		}
	) {
		if (!this.planAccepted) {
			return;
		}
		const isSharedParticipant =
			this.sharedPlanState?.sharedSession?.planAccepted === true && !this.isSharedPlanHost();
		if (isSharedParticipant && options?.skipSharedJoin !== true) {
			void this.joinSharedPlanStepById(step.id, {
				announce: false,
				navigate: false,
				emitGuidance: false,
			});
		}
		const isHostStepAdvance = Boolean(
			this.sharedPlanState?.sharedSession?.planAccepted &&
			this.isSharedPlanHost() &&
			step.id !== this.sharedPlanState.sharedSession.activeStepId
		);
		if (isHostStepAdvance) {
			void this.advanceSharedPlanToStep(step.id);
		}
		const navigationSource =
			options?.navigationSource ||
			(isHostStepAdvance ? 'host_step_advance' : 'plan_step_select');
		this.resetIdleAssistTimer();
		this.activePlanStepId = step.id;
		const board = step.board || 'inspire';
		const graph = globalThis.neuroCreate.graph;
		const stayCollaborative =
			this.sharedPlanState?.sharedSession?.planAccepted === true ||
			(graph?.graphMode === board && graph.isCollaborative === true);
		const targetHash = this.getHashForBoard(board, stayCollaborative);
		const currentHash = window.location.hash || '#inspire';
		const emitGuidance = options?.emitGuidance !== false;
		this.pendingPlanStepSwitch = {
			board,
			stepId: step.id,
			buildingBlockId: step.buildingBlockId || null,
			source: navigationSource,
			emitGuidance,
		};
		if (currentHash !== targetHash) {
			window.location.hash = targetHash;
		} else {
			this.pendingPlanStepSwitch = null;
			if (emitGuidance) {
				// Same board: no hashchange event, so dispatch directly once.
				void this.postAgentEvent('board_switched', {
					board,
					source: navigationSource,
					buildingBlockId: step.buildingBlockId || null,
					isCollaborative: globalThis.neuroCreate.graph?.isCollaborative === true,
				});
			}
		}
		this.requestUpdate();
		this.scrollMessagesToBottom();
	}

	private async advanceSharedPlanToStep(targetStepId: string) {
		if (!this.isSharedPlanHost() || this.isBusy) return;
		const response = await fetch(`/board/${this.boardId}/agent/shared-plan/advance`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sessionId: this.sessionId, targetStepId }),
		}).catch(() => null);
		if (!response?.ok) return;
		const body = (await response.json()) as SharedPlanState;
		this.syncSharedPlanState(body);
		this.requestUpdate();
	}

	private async joinSharedPlanStepById(
		stepId: string | null,
		options: { announce?: boolean; navigate?: boolean; emitGuidance?: boolean } = {}
	): Promise<SharedPlanState | null> {
		if (this.isBusy) return null;
		const step =
			(stepId && this.plan?.steps?.find((candidate) => candidate.id === stepId)) ||
			this.getSharedActiveStep();
		const response = await fetch(`/board/${this.boardId}/agent/shared-plan/join`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				sessionId: this.sessionId,
				stepId: step?.id || null,
			}),
		}).catch(() => null);
		if (!response?.ok) return null;
		const body = (await response.json()) as SharedPlanState;
		this.syncSharedPlanState(body);
		const stepToJoin =
			(step?.id && this.plan?.steps?.find((candidate) => candidate.id === step.id)) ||
			this.getSharedActiveStep() ||
			step;
		if (stepToJoin) {
			if (options.announce !== false) {
				this.addNeutralEventMessage(`Joined team step: ${stepToJoin.label}`);
			}
			if (options.navigate !== false) {
				this.onPlanBlockClick(stepToJoin, {
					emitGuidance: options.emitGuidance !== false,
					allowSharedParticipantNavigation: true,
					skipSharedJoin: true,
					navigationSource: 'shared_step_join',
				});
			}
		}
		this.requestUpdate();
		return body;
	}

	private async advanceSharedPlan(event: Event) {
		event.preventDefault();
		if (!this.isSharedPlanHost() || this.isBusy) return;
		const response = await fetch(`/board/${this.boardId}/agent/shared-plan/advance`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sessionId: this.sessionId }),
		}).catch(() => null);
		if (!response?.ok) return;
		const body = (await response.json()) as SharedPlanState;
		this.syncSharedPlanState(body);
		const activeStep = this.getSharedActiveStep();
		if (activeStep) {
			this.addNeutralEventMessage(`Team step: ${activeStep.label}`);
			this.onPlanBlockClick(activeStep, {
				emitGuidance: true,
				navigationSource: 'host_step_advance',
			});
		}
		this.requestUpdate();
	}

	private async joinSharedPlanStep(event?: Event) {
		event?.preventDefault();
		await this.joinSharedPlanStepById(this.getSharedActiveStep()?.id || null, {
			announce: true,
			navigate: true,
			emitGuidance: true,
		});
	}

	private async requestSharedCatchupMessage({
		automatic = false,
	}: { automatic?: boolean } = {}): Promise<boolean> {
		if (!this.boardId || !this.sessionId) return false;
		if (this.isBusy) {
			// Wait briefly for an in-flight turn so join catch-up is not silently dropped.
			const startedAt = Date.now();
			while (this.isBusy && Date.now() - startedAt < 8_000) {
				// eslint-disable-next-line no-await-in-loop
				await new Promise<void>((resolve) => window.setTimeout(resolve, 100));
			}
			if (this.isBusy) return false;
		}
		this.isBusy = true;
		this.isMinimized = false;
		this.workingLabel = automatic ? 'Joining the shared session...' : 'Catching you up...';
		this.requestUpdate();
		const response = await fetch(`/board/${this.boardId}/agent/shared-plan/catch-up`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sessionId: this.sessionId }),
		}).catch(() => null);
		this.isBusy = false;
		this.workingLabel = '';
		if (!response?.ok) {
			this.requestUpdate();
			return false;
		}
		const body = (await response.json()) as Partial<AgentHttpReply> & SharedPlanState;
		this.syncSharedPlanState({
			sharedSession: body.sharedSession || null,
			participantState: body.participantState || null,
		});
		await this.streamAssistantMessage(body.message || 'You are caught up.');
		this.requestUpdate();
		return true;
	}

	private requestSharedCatchup(event: Event) {
		event.preventDefault();
		void this.requestSharedCatchupMessage();
	}

	private onPlanStepDragStart(event: DragEvent, step: AgentPlanStep) {
		if (this.planAccepted || !this.canEditPlan() || this.isPlanSaving) {
			event.preventDefault();
			return;
		}
		this.draggedPlanStepId = step.id;
		event.dataTransfer?.setData('text/plain', step.id);
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = 'move';
		}
	}

	private onPlanStepDragOver(event: DragEvent) {
		if (!this.planAccepted && this.canEditPlan() && this.draggedPlanStepId) {
			event.preventDefault();
			if (event.dataTransfer) {
				event.dataTransfer.dropEffect = 'move';
			}
		}
	}

	private onPlanStepDrop(event: DragEvent, targetStep: AgentPlanStep) {
		if (this.planAccepted || !this.canEditPlan() || this.isPlanSaving || !this.plan?.steps?.length) {
			return;
		}
		event.preventDefault();
		const draggedId = this.draggedPlanStepId || event.dataTransfer?.getData('text/plain');
		if (!draggedId || draggedId === targetStep.id) {
			this.draggedPlanStepId = null;
			return;
		}
		const previousPlan = this.plan;
		const steps = [...this.plan.steps];
		const fromIndex = steps.findIndex((step) => step.id === draggedId);
		const toIndex = steps.findIndex((step) => step.id === targetStep.id);
		if (fromIndex < 0 || toIndex < 0) {
			this.draggedPlanStepId = null;
			return;
		}
		const [draggedStep] = steps.splice(fromIndex, 1);
		steps.splice(toIndex, 0, draggedStep);
		this.plan = { ...this.plan, steps };
		this.draggedPlanStepId = null;
		this.requestUpdate();
		void this.savePendingPlan(
			steps.map((step) => step.buildingBlockId).filter((id): id is string => Boolean(id)),
			previousPlan
		);
	}

	private onPlanStepDragEnd() {
		this.draggedPlanStepId = null;
	}

	private getAvailablePlanBuildingBlocks(): FlowyBuildingBlockOption[] {
		const currentIds = new Set(
			(this.plan?.steps || [])
				.map((step) => step.buildingBlockId)
				.filter((id): id is string => Boolean(id))
		);
		return this.planBuildingBlocks.filter((block) => !currentIds.has(block.id));
	}

	private async savePendingPlan(buildingBlockIds: string[], fallbackPlan: AgentPlan) {
		if (
			this.planAccepted ||
			!this.canEditPlan() ||
			this.isPlanSaving ||
			buildingBlockIds.length < 1 ||
			buildingBlockIds.length > 10
		) {
			return;
		}

		this.isPlanSaving = true;
		this.requestUpdate();
		const response = await fetch(`/board/${this.boardId}/agent/plan`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sessionId: this.sessionId, buildingBlockIds }),
		}).catch(() => null);

		if (response?.ok) {
			const body = (await response.json()) as { plan?: AgentPlan };
			if (body.plan?.steps?.length) {
				this.plan = body.plan;
			}
			if (this.workingLabel === 'Could not save the plan change.') {
				this.workingLabel = '';
			}
		} else {
			this.plan = fallbackPlan;
			this.workingLabel = 'Could not save the plan change.';
		}
		this.isPlanSaving = false;
		this.requestUpdate();
	}

	private removePendingPlanStep(event: Event, step: AgentPlanStep) {
		event.preventDefault();
		event.stopPropagation();
		if (
			this.planAccepted ||
			!this.canEditPlan() ||
			this.isPlanSaving ||
			!this.plan?.steps ||
			this.plan.steps.length <= 1
		) {
			return;
		}
		const previousPlan = this.plan;
		const steps = this.plan.steps.filter((candidate) => candidate.id !== step.id);
		this.plan = { ...this.plan, steps };
		void this.savePendingPlan(
			steps.map((candidate) => candidate.buildingBlockId).filter((id): id is string => Boolean(id)),
			previousPlan
		);
	}

	private addPendingPlanStep(block: FlowyBuildingBlockOption) {
		if (
			this.planAccepted ||
			!this.canEditPlan() ||
			this.isPlanSaving ||
			!this.plan?.steps ||
			this.plan.steps.length >= 10
		) {
			return;
		}
		const previousPlan = this.plan;
		const buildingBlockIds = [
			...this.plan.steps
				.map((step) => step.buildingBlockId)
				.filter((id): id is string => Boolean(id)),
			block.id,
		];
		this.isPlanAddMenuOpen = false;
		void this.savePendingPlan(buildingBlockIds, previousPlan);
	}

	private togglePlanAddMenu(event: Event) {
		event.preventDefault();
		if (!this.canEditPlan() || this.planAccepted) return;
		this.isPlanAddMenuOpen = !this.isPlanAddMenuOpen;
		this.requestUpdate();
	}

	private onNextStepHintClick(hint: NextStepHint) {
		const matchingStep = this.plan?.steps?.find(
			(step) => step.buildingBlockId === hint.buildingBlockId
		);
		if (matchingStep) {
			this.onPlanBlockClick(matchingStep, {
				emitGuidance: true,
				navigationSource: 'plan_step_select',
			});
			return;
		}
		// Fallback: building block isn't in the current plan, just switch board.
		const graph = globalThis.neuroCreate.graph;
		const stayCollaborative =
			this.sharedPlanState?.sharedSession?.planAccepted === true ||
			(graph?.graphMode === hint.board && graph.isCollaborative === true);
		const targetHash = this.getHashForBoard(hint.board, stayCollaborative);
		const currentHash = window.location.hash || '#inspire';
		this.pendingPlanStepSwitch = {
			board: hint.board,
			stepId: '',
			buildingBlockId: hint.buildingBlockId,
			source: 'plan_step_select',
		};
		if (currentHash !== targetHash) {
			window.location.hash = targetHash;
		} else {
			this.pendingPlanStepSwitch = null;
			void this.postAgentEvent('board_switched', {
				board: hint.board,
				source: 'plan_step_select',
				buildingBlockId: hint.buildingBlockId,
			});
		}
		this.requestUpdate();
	}

	private async acceptPlan(event?: Event) {
		event?.preventDefault();
		if (
			this.isBusy ||
			this.isPlanSaving ||
			!this.canEditPlan() ||
			!this.plan?.steps?.length ||
			this.planAccepted
		) {
			return;
		}

		const response = await fetch(`/board/${this.boardId}/agent/accept-plan`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				sessionId: this.sessionId,
				buildingBlockIds: this.plan.steps
					.map((step) => step.buildingBlockId)
					.filter((id): id is string => Boolean(id)),
			}),
		}).catch(() => null);

		if (response?.ok) {
			const body = (await response.json()) as Partial<AgentHttpReply> & {
				planAccepted?: boolean;
				plan?: AgentPlan;
			};
			this.applyIncomingPlanAccepted(body.planAccepted);
			this.isPlanAddMenuOpen = false;
			if (body.plan?.steps?.length) {
				this.plan = body.plan;
			}
			await this.loadSharedPlanState();
			this.syncPlanSidebarState();
			if (this.plan?.steps?.length && this.planAccepted) {
				const firstStep = this.plan.steps[0];
				const acceptedMessage =
					body.message ||
					`Great, let's start with the first step of our plan: **${firstStep.label}**. You can now click plan steps to move through the session.`;
				const acceptedNavigationHint = 'You can now click plan steps to move through the session.';
				const messageWithNavigationHint = acceptedMessage.includes(acceptedNavigationHint)
					? acceptedMessage
					: `${acceptedMessage}\n\n${acceptedNavigationHint}`;
				await this.streamAssistantMessage(
					body.message ? messageWithNavigationHint : acceptedMessage
				);
				this.onPlanBlockClick(firstStep, {
					emitGuidance: true,
					navigationSource: 'plan_acceptance',
				});
			}
		}
		this.requestUpdate();
	}

	private renderQuickReplies(message: AgentPanelMessage, isActive: boolean) {
		const replies = message.quickReplies || [];
		if (!replies.length || !isActive || message.role !== 'assistant') {
			return '';
		}
		return html`
			<div class="agent-chat-panel__quick-replies">
				${replies.map(
					(reply) => html`
						<button
							type="button"
							class="agent-chat-panel__quick-reply"
							?disabled=${this.isBusy || this.isPlanSaving}
							@click=${() => this.onQuickReplyClick(reply)}
						>
							${reply.label}
						</button>
					`
				)}
			</div>
		`;
	}

	private getActiveQuickReplyMessageIndex(): number {
		const activeIndex = findActiveQuickReplyMessageIndex(this.messages);
		if (
			activeIndex >= 0 &&
			this.directionPromptDismissed &&
			activeIndex === findOpeningQuickReplyMessageIndex(this.messages)
		) {
			return -1;
		}
		return activeIndex;
	}

	private getDirectionPromptStorageKey(): string {
		return `flowy-direction-prompt-dismissed:${this.boardId}:${this.sessionId}`;
	}

	private restoreDirectionPromptDismissal(): void {
		this.directionPromptDismissed =
			window.localStorage.getItem(this.getDirectionPromptStorageKey()) === '1';
		this.directionPromptExpanded = false;
	}

	private dismissDirectionPrompt(): void {
		this.directionPromptDismissed = true;
		this.directionPromptExpanded = false;
		window.localStorage.setItem(this.getDirectionPromptStorageKey(), '1');
	}

	private renderDirectionPrompt() {
		if (this.directionPromptDismissed || this.hasPlanSteps()) return '';
		if (!shouldShowDirectionPrompt(this.messages)) return '';
		const messageIndex = findOpeningQuickReplyMessageIndex(this.messages);
		if (messageIndex < 0) return '';
		const message = this.messages[messageIndex];
		return html`
			<aside class="agent-chat-panel__direction-prompt" aria-label="Board goal">
				<div class="agent-chat-panel__direction-prompt-copy">
					<strong>Set a board goal</strong>
					<span>Choose a goal so Flowy can draft a short plan.</span>
				</div>
				<div class="agent-chat-panel__direction-prompt-actions">
					<button
						type="button"
						class="agent-chat-panel__direction-prompt-toggle"
						aria-expanded=${String(this.directionPromptExpanded)}
						@click=${() => {
							this.directionPromptExpanded = !this.directionPromptExpanded;
						}}
					>
						${this.directionPromptExpanded ? 'Hide choices' : 'Choose goal'}
					</button>
					<button
						type="button"
						class="agent-chat-panel__direction-prompt-dismiss"
						aria-label="Continue without a plan"
						title="Continue without a plan"
						@click=${() => this.dismissDirectionPrompt()}
					>
						×
					</button>
				</div>
				${this.directionPromptExpanded
					? html`<div class="agent-chat-panel__direction-prompt-choices">
							${this.renderQuickReplies(message, true)}
						</div>`
					: ''}
			</aside>
		`;
	}

	private hasPendingQuickReply(): boolean {
		return this.getActiveQuickReplyMessageIndex() >= 0;
	}

	private renderMessage(message: AgentPanelMessage, index: number) {
		const isLong = message.content.length > FLOWY_LONG_MESSAGE_CHARS;
		const hasEventDetails = message.role === 'event' && hasExpandableEventDetails(message.content);
		const isExpanded = this.expandedMessageIndexes.includes(index);
		const displayContent =
			hasEventDetails && !isExpanded
				? getEventStatusSummary(message.content)
				: isLong && !isExpanded
				? truncateFlowyText(message.content, {
						maxChars: FLOWY_LONG_MESSAGE_CHARS,
						preserveCompleteSentence: true,
					})
				: message.content;
		const safeHtml = this.injectNodeReferences(parseFlowyMarkdown(displayContent));
		const hint = message.nextStepHint || null;
		const activeQuickReplyIndex = this.getActiveQuickReplyMessageIndex();
		const quickRepliesActive = index === activeQuickReplyIndex;
		return html`
			<div class="agent-chat-panel__message agent-chat-panel__message--${message.role}">
				${message.role === 'event'
					? ''
					: message.role === 'tool'
					? ''
					: html`<div class="agent-chat-panel__message-role"
							>${message.role === 'user' ? 'You' : 'Flowy'}</div
						>`}
				<div class="agent-chat-panel__message-body">${unsafeHTML(safeHtml)}</div>
				${hasEventDetails || (isLong && (message.role === 'assistant' || message.role === 'user'))
					? html`
							<button
								type="button"
								class="agent-chat-panel__message-expand"
								@click=${() => this.toggleMessageExpanded(index)}
							>
								${isExpanded ? 'Collapse' : 'Expand'}
							</button>
						`
					: ''}
				${this.renderQuickReplies(message, quickRepliesActive)}
				${hint
					? html`
							<button
								type="button"
								class="agent-chat-panel__next-step-button"
								@click=${() => this.onNextStepHintClick(hint)}
							>
								Go to ${hint.label} →
							</button>
						`
					: ''}
			</div>
		`;
	}

	private toggleMessageExpanded(index: number) {
		if (this.expandedMessageIndexes.includes(index)) {
			this.expandedMessageIndexes = this.expandedMessageIndexes.filter((entry) => entry !== index);
		} else {
			this.expandedMessageIndexes = [...this.expandedMessageIndexes, index];
		}
	}

	private renderToolSteps() {
		if (!this.toolSteps.length) {
			return '';
		}

		return html`
			<div class="agent-chat-panel__steps agent-chat-panel__steps--inline">
				${this.toolSteps.map(
					(step) => html`
						<div class="agent-chat-panel__step">
							<span class="agent-chat-panel__step-status agent-chat-panel__step-status--${step.status}">
								${step.status}
							</span>
							<span class="agent-chat-panel__step-label">
								${step.label}
								${step.rationale ? html`<small>${step.rationale}</small>` : ''}
							</span>
						</div>
					`
				)}
			</div>
		`;
	}

	private renderLiveAssistantMessage() {
		if (!this.isBusy && !this.streamingAssistantContent && !this.workingLabel && !this.toolSteps.length) {
			return '';
		}

		const safeHtml = this.streamingAssistantContent
			? this.injectNodeReferences(parseFlowyMarkdown(this.streamingAssistantContent))
			: '';

		return html`
			<div class="agent-chat-panel__message agent-chat-panel__message--assistant agent-chat-panel__message--live">
				<div class="agent-chat-panel__message-role">Flowy</div>
				${this.workingLabel
					? html`<div class="agent-chat-panel__working agent-chat-panel__working--inline">${this.workingLabel}</div>`
					: ''}
				${this.renderToolSteps()}
				${this.streamingAssistantContent
					? html`<div class="agent-chat-panel__message-body">${unsafeHTML(safeHtml)}</div>`
					: ''}
			</div>
		`;
	}

	private renderPanelToggleIcon() {
		if (this.isMinimized) {
			return html`
				<span class="agent-chat-panel__toggle-icon" aria-hidden="true">
					<svg viewBox="0 0 24 24" focusable="false">
						<path
							d="M12 5v14M5 12h14"
							fill="none"
							stroke="currentColor"
							stroke-width="1.75"
							stroke-linecap="round"
						></path>
					</svg>
				</span>
			`;
		}
		return html`
			<span class="agent-chat-panel__toggle-icon" aria-hidden="true">
				<svg viewBox="0 0 24 24" focusable="false">
					<path
						d="M5 12h14"
						fill="none"
						stroke="currentColor"
						stroke-width="1.75"
						stroke-linecap="round"
					></path>
				</svg>
			</span>
		`;
	}

	private getSessionLogExportJson(): string {
		return JSON.stringify(
			{
				exportedAt: new Date().toISOString(),
				boardId: this.boardId,
				sessionId: this.sessionId,
				entryCount: this.sessionLogEntries.length,
				entries: this.sessionLogEntries,
			},
			null,
			2
		);
	}

	private async copySessionLog(event: Event) {
		event.stopPropagation();
		const json = this.getSessionLogExportJson();
		const writePromise = navigator.clipboard?.writeText(json);
		if (!writePromise) {
			console.log('[FlowySessionLog]', json);
			return;
		}
		await writePromise.catch(() => {
			console.log('[FlowySessionLog]', json);
		});
	}

	private downloadSessionLog(event: Event) {
		event.stopPropagation();
		const blob = new Blob([this.getSessionLogExportJson()], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `flowy-session-${this.sessionId || 'unknown'}.json`;
		document.body.append(anchor);
		anchor.click();
		anchor.remove();
		URL.revokeObjectURL(url);
	}

	private renderPlanChevron(expanded: boolean) {
		if (expanded) {
			return html`<svg viewBox="0 0 24 24" focusable="false">
				<path d="M14.71 6.71a1 1 0 0 0-1.41 0L9.83 10.17a1 1 0 0 0 0 1.41l3.47 3.47a1 1 0 0 0 1.41-1.41L11.41 12l3.3-3.29a1 1 0 0 0 0-1.41z"></path>
			</svg>`;
		}
		return html`<svg viewBox="0 0 24 24" focusable="false">
			<path d="M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.41 1.41l4.59-4.59a1 1 0 0 0 0-1.41L10.7 6.7a1 1 0 0 0-1.41.01z"></path>
		</svg>`;
	}

	private renderPlanStepButton(step: AgentPlanStep) {
		const active = this.activePlanStepId === step.id;
		const pending = !this.planAccepted;
		const editablePending = pending && this.canEditPlan();
		const disabled = pending;
		const title = editablePending
			? 'Drag to reorder. Accept the plan to use steps as navigation.'
			: pending
				? 'Only the host can edit and accept this plan.'
				: this.isSharedPlanHost()
				? 'Go to this step'
				: 'Join this step for your view';
		return html`
			<li class="agent-chat-panel__plan-bullet">
				<div class="agent-chat-panel__plan-step-row">
					<button
						type="button"
						class="agent-chat-panel__plan-block ${active
							? 'agent-chat-panel__plan-block--active'
							: ''} ${pending ? 'agent-chat-panel__plan-block--pending' : ''}"
						style=${this.planAccentStyle(step.colour)}
						draggable=${String(editablePending && !this.isPlanSaving)}
						aria-disabled=${String(disabled)}
						title=${title}
						@click=${() => this.onPlanBlockClick(step)}
						@dragstart=${(event: DragEvent) => this.onPlanStepDragStart(event, step)}
						@dragover=${this.onPlanStepDragOver}
						@drop=${(event: DragEvent) => this.onPlanStepDrop(event, step)}
						@dragend=${this.onPlanStepDragEnd}
					>
						${editablePending
							? html`<span class="agent-chat-panel__plan-drag-handle" aria-hidden="true"
									><span></span><span></span><span></span
								></span>`
							: ''}
						<span class="agent-chat-panel__plan-block-label">${step.label}</span>
					</button>
					${editablePending
						? html`
								<button
									type="button"
									class="agent-chat-panel__plan-remove-step"
									aria-label=${`Remove ${step.label} from plan`}
									title=${this.plan?.steps?.length === 1
										? 'A plan needs at least one step'
										: `Remove ${step.label}`}
									?disabled=${this.isPlanSaving || this.plan?.steps?.length === 1}
									@click=${(event: Event) => this.removePendingPlanStep(event, step)}
								>
									<span aria-hidden="true">×</span>
								</button>
							`
						: ''}
				</div>
			</li>
		`;
	}

	private renderPlanAddStep() {
		if (this.planAccepted || !this.canEditPlan() || !this.plan?.steps?.length) return '';
		const availableBlocks = this.getAvailablePlanBuildingBlocks();
		const atLimit = this.plan.steps.length >= 10;
		return html`
			<div class="agent-chat-panel__plan-add">
				<button
					type="button"
					class="agent-chat-panel__plan-add-toggle"
					?disabled=${this.isPlanSaving || atLimit || !availableBlocks.length}
					aria-expanded=${String(this.isPlanAddMenuOpen)}
					@click=${(event: Event) => this.togglePlanAddMenu(event)}
				>
					<span aria-hidden="true">＋</span> Add step
				</button>
				${this.isPlanAddMenuOpen && !atLimit
					? html`
							<div class="agent-chat-panel__plan-add-menu" role="menu" aria-label="Add a plan step">
								${availableBlocks.map(
									(block) => html`
										<button
											type="button"
											class="agent-chat-panel__plan-add-option"
											style=${this.planAccentStyle(block.colourVar)}
											role="menuitem"
											@click=${() => this.addPendingPlanStep(block)}
										>
											<span>${block.label}</span>
											<small>${block.board === 'moodboard' ? 'Synthesise' : block.board}</small>
										</button>
									`
								)}
							</div>
						`
					: ''}
			</div>
		`;
	}

	private isResearchContextActive(): boolean {
		if (this.lastSessionState?.activeBuildingBlockId === 'research_related_work') {
			return true;
		}
		const activeStep = this.plan?.steps?.find((step) => step.id === this.activePlanStepId);
		if (activeStep?.buildingBlockId === 'research_related_work') {
			return true;
		}
		return Boolean(this.plan?.steps?.some((step) => step.buildingBlockId === 'research_related_work'));
	}

	private formatResearchSourceHint(sources: string[]): string {
		if (!sources.length) return 'web';
		return sources
			.map((source) => {
				if (source === 'gdelt') return 'GDELT';
				if (source === 'pubmed') return 'PubMed';
				return source;
			})
			.join(' · ');
	}

	private renderResearchLensesSection() {
		if (!this.researchLenses.length || !this.isResearchContextActive()) {
			return '';
		}

		const used = new Set(this.lastUsedResearchLensLabels.map((label) => label.toLowerCase()));

		return html`
			<div class="agent-chat-panel__research-lenses">
				<h4 class="agent-chat-panel__research-lenses-heading">Research angles</h4>
				<p class="agent-chat-panel__research-lenses-intro">
					${this.boardEngine
						? `${this.boardEngine} engine — each angle searches a different kind of evidence.`
						: 'Each angle searches a different kind of evidence.'}
				</p>
				<ul class="agent-chat-panel__research-lens-list">
					${this.researchLenses.map(
						(lens) => html`
							<li
								class="agent-chat-panel__research-lens ${used.has(lens.label.toLowerCase())
									? 'agent-chat-panel__research-lens--used'
									: ''}"
								title=${lens.purpose}
							>
								<span class="agent-chat-panel__research-lens-label">${lens.label}</span>
								<span class="agent-chat-panel__research-lens-purpose">${lens.purpose}</span>
								<span class="agent-chat-panel__research-lens-sources"
									>${this.formatResearchSourceHint(lens.preferredSources)}</span
								>
							</li>
						`
					)}
				</ul>
			</div>
		`;
	}

	private renderSharedPlanActions() {
		const sharedSession = this.sharedPlanState?.sharedSession || null;
		if (!sharedSession?.planAccepted || !this.plan?.steps?.length) {
			return '';
		}
		const activeStep = this.getSharedActiveStep();
		if (!activeStep) {
			return '';
		}
		const participantState = this.sharedPlanState?.participantState || null;
		const joinedCurrentStep = participantState?.joinedStepId === activeStep.id;
		if (this.isSharedPlanHost()) {
			return html`
				<div class="agent-chat-panel__shared-plan-actions">
					<p class="agent-chat-panel__shared-plan-caption">Team step: ${activeStep.label}</p>
					<button
						type="button"
						class="agent-chat-panel__accept-plan"
						?disabled=${this.isBusy}
						@click=${this.advanceSharedPlan}
					>
						Advance step
					</button>
				</div>
			`;
		}
		return html`
			<div class="agent-chat-panel__shared-plan-actions">
				<p class="agent-chat-panel__shared-plan-caption">Team step: ${activeStep.label}</p>
				${joinedCurrentStep
					? html`<p class="agent-chat-panel__shared-plan-caption">You are on this step.</p>`
					: html`
							<button
								type="button"
								class="agent-chat-panel__accept-plan"
								?disabled=${this.isBusy}
								@click=${this.joinSharedPlanStep}
							>
								Join this step
							</button>
						`}
			</div>
		`;
	}

	private getDisplaySessionGoal(): string {
		const sharedGoal = this.sharedPlanState?.sharedSession?.goal || null;
		return (this.sessionGoal || sharedGoal || '').trim();
	}

	private renderSessionGoalBlock() {
		const goal = this.getDisplaySessionGoal();
		return html`
			<section class="agent-chat-panel__session-goal" aria-label="Board goal">
				<h3 class="agent-chat-panel__section-heading">Goal</h3>
				<p class="agent-chat-panel__session-goal-text">
					${goal || 'Choose a board goal with Flowy.'}
				</p>
			</section>
		`;
	}

	private renderPlanSidebarContent() {
		if (!this.plan?.steps?.length) {
			return '';
		}

		return html`
			<div class="agent-chat-panel__plan-sidebar-content">
				${this.renderSessionGoalBlock()}
				<h3 class="agent-chat-panel__plan-heading agent-chat-panel__section-heading">
					Workflow plan
				</h3>
				<ul class="agent-chat-panel__plan-list">
					${this.plan.steps.map((step) => this.renderPlanStepButton(step))}
				</ul>
				${this.renderPlanAddStep()}
				${!this.planAccepted && this.canEditPlan()
					? html`
							<button
								type="button"
								class="agent-chat-panel__accept-plan"
								?disabled=${this.isBusy || this.isPlanSaving}
								@click=${this.acceptPlan}
							>
								Accept plan
							</button>
						`
					: ''}
				${this.renderSharedPlanActions()}
			</div>
		`;
	}

	private renderPlanIcon() {
		return html`
			<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
				<path
					d="M9 6.75h10.5M9 12h10.5M9 17.25h10.5"
					fill="none"
					stroke="currentColor"
					stroke-width="1.75"
					stroke-linecap="round"
				></path>
				<circle cx="5.25" cy="6.75" r="1.1" fill="currentColor"></circle>
				<circle cx="5.25" cy="12" r="1.1" fill="currentColor"></circle>
				<circle cx="5.25" cy="17.25" r="1.1" fill="currentColor"></circle>
			</svg>
		`;
	}

	private renderPlanSidebar() {
		if (!this.hasPlanSteps()) {
			return '';
		}

		const collapsed = this.isPlanCollapsed;

		return html`
			<aside
				class="agent-chat-panel__plan-sidebar ${collapsed ? 'agent-chat-panel__plan-sidebar--collapsed' : ''}"
				aria-label="Session plan"
			>
				<button
					type="button"
					class="agent-chat-panel__plan-sidebar-toggle"
					@click=${this.togglePlanCollapsed}
					aria-expanded=${String(!collapsed)}
					aria-label=${collapsed ? 'Expand plan panel' : 'Collapse plan panel'}
					title=${collapsed ? 'Show plan' : 'Collapse plan'}
				>
					<span class="agent-chat-panel__plan-sidebar-icon" aria-hidden="true">
						${this.renderPlanIcon()}
					</span>
					<span class="agent-chat-panel__plan-sidebar-toggle-label">
						${collapsed ? 'Plan' : 'Collapse plan'}
					</span>
					<span class="agent-chat-panel__plan-chevron" aria-hidden="true">
						${this.renderPlanChevron(!collapsed)}
					</span>
				</button>
				<div class="agent-chat-panel__plan-sidebar-body" aria-hidden=${String(collapsed)}>
					${this.renderPlanSidebarContent()}
				</div>
			</aside>
		`;
	}

	private renderSlashCommandMenu() {
		if (!this.isSlashCommandMenuOpen || this.isBusy) {
			return '';
		}
		const commands = this.getSlashCommandsForCurrentBoard();
		return html`
			<div class="agent-chat-panel__slash-menu" role="menu" aria-label="Flowy commands">
				${commands.map(
					(command) => html`
						<button
							type="button"
							class="agent-chat-panel__slash-command"
							role="menuitem"
							@keydown=${this.onSlashCommandButtonKeydown}
							@click=${() => void this.runSlashCommand(command.value)}
						>
							<span class="agent-chat-panel__slash-command-label">/${command.label}</span>
							<span class="agent-chat-panel__slash-command-hint">${command.hint}</span>
						</button>
					`
				)}
			</div>
		`;
	}

	render() {
		const hasPlan = this.hasPlanSteps();
		return html`
			<section
				class="agent-chat-panel ${this.isMinimized ? 'agent-chat-panel--minimized' : ''} ${hasPlan
					? 'agent-chat-panel--has-plan'
					: ''} ${hasPlan && this.isPlanCollapsed ? 'agent-chat-panel--plan-collapsed' : ''}"
			>
				<header
					class="agent-chat-panel__header"
					@click=${this.isMinimized ? this.toggleMinimized : null}
				>
					<div class="agent-chat-panel__header-main" @pointerdown=${this.onDragStart}>
						<div class="agent-chat-panel__brand" aria-label="Neurocreate">
							<img
								class="agent-chat-panel__brand-logo"
								src="/assets/neurocreate_icon_only.png"
								alt="Neurocreate"
							/>
							${this.contextModeStatus
								? html`<span
										class="agent-chat-panel__mode-status"
										title=${this.contextModeStatus.description}
										aria-label=${this.contextModeStatus.description}
									>
										${this.contextModeStatus.label}
									</span>`
								: ''}
						</div>
					</div>
					<div class="agent-chat-panel__header-actions">
						${this.isMinimized
							? ''
							: html`
									${this.isSessionLoggingEnabled()
										? html`
												<button
													type="button"
													class="agent-chat-panel__reset"
													aria-label="Copy Flowy session log"
													title="Copy session log"
													@click=${this.copySessionLog}
												>
													Copy log
												</button>
												<button
													type="button"
													class="agent-chat-panel__reset"
													aria-label="Download Flowy session log"
													title="Download session log"
													@click=${this.downloadSessionLog}
												>
													Download log
												</button>
										  `
										: ''}
									<button
										type="button"
										class="agent-chat-panel__reset"
										aria-label="Reset Flowy session"
										@click=${(event: Event) => {
											event.stopPropagation();
											void this.resetSession();
										}}
									>
										Reset
									</button>
								`}
						<button
							type="button"
							class="agent-chat-panel__toggle"
							aria-label=${this.isMinimized ? 'Expand Flowy' : 'Minimize Flowy'}
							@click=${(event: Event) => {
								event.stopPropagation();
								this.toggleMinimized();
							}}
						>
							${this.renderPanelToggleIcon()}
						</button>
					</div>
				</header>
				<div class="agent-chat-panel__body" aria-hidden=${String(this.isMinimized)}>
					<div class="agent-chat-panel__main-layout">
						<div class="agent-chat-panel__chat-column">
							<div class="agent-chat-panel__messages">
								${this.messages.length
									? this.messages.map((message, index) => this.renderMessage(message, index))
									: html`
											<div class="agent-chat-panel__empty">
												Tell Flowy what you want to achieve in this session and it will help you
												plan, search, and use tools without taking over the thinking.
											</div>
										`}
								${this.renderLiveAssistantMessage()}
							</div>
							${this.renderDirectionPrompt()}
							<form class="agent-chat-panel__composer" @submit=${this.sendMessage}>
								${this.renderSlashCommandMenu()}
								<textarea
									class="agent-chat-panel__input"
									rows="2"
									placeholder="Ask Flowy, or type / for commands..."
									?disabled=${this.isBusy || this.isPlanSaving}
									@input=${this.onComposerInput}
									@keydown=${this.onComposerKeydown}
								></textarea>
								<button
									class="agent-chat-panel__send"
									type="submit"
									?disabled=${this.isBusy || this.isPlanSaving}
								>
									Send
								</button>
							</form>
						</div>
						${this.renderPlanSidebar()}
					</div>
				</div>
			</section>
		`;
	}
}

if (!customElements.get('agent-chat-panel')) {
	customElements.define('agent-chat-panel', AgentChatPanel);
}
